import { useState, useEffect } from 'react';
import {
  getAdminWistiaStats, getAdminLeadsStats,
  getAdminFunnelPageStats, getAdminRySalesStats,
  getAdminInterstitialFunnel,
  getAdminCtaDetail,
  getBusinessPlan,
} from '../../utils/api';
import { DEFAULT_RATES, display as displayCurrency } from './revenueShared';
import { C, FONT, SectionHeader, MiniBarChart, TimeframeControlWithDates, useTimeframe, rangeLabel, DEFAULT_MARKER_NAME, returningLine } from './adminShared';
import { DropOffLink } from './DropOffModal';
import { HeatMapLink } from './SalesHeatmap';
import { useSalesVideoWindows, salesExtra } from './salesVideoPeriods';

// ── Small helpers ────────────────────────────────────────────────────────────

const fmtTime = (secs) => {
  if (!secs || secs === 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// Per-page accent colours — existing tokens only.
const ACCENT = {
  optin: C.accent,   // clay/terracotta
  mc:    C.heading,  // brown
  ry:    C.red,      // rust
};

// Opt-in conversion rate for the two supporting lines under the opt-in tiles.
// A dash rather than 0% or NaN when there is no denominator — a rate over zero
// loads is not zero, it is unknown. One decimal below 10%, because at these
// volumes whole numbers lose the difference between 6% and 6.8%; whole numbers
// above that, where the extra digit is noise.
const optinRate = (numerator, denominator) => {
  const n = Number(numerator), d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return '—';
  const pct = (n / d) * 100;
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
};

// ── EVERY PERCENTAGE NAMES ITS OWN DENOMINATOR ──────────────────────────────
// Ben's rule: a percentage on these cards is a share of UNIQUE VISITORS — every
// person who reached the page — not of engaged visitors. "76% watched" meant 76%
// of the people who already did something, which flatters every rate on the card.
//
// COMPUTED HERE, NOT READ FROM THE ENDPOINT. The API still returns playRate and
// cta.*.pct, and both divide by ENGAGED visitors; using them would silently give
// the old basis. Passing the denominator in at each call site is the point — the
// Funnel Overview carried exactly this bug (percentages divided by whatever row
// sat above them, so inserting a row re-pointed the one below it and nobody
// noticed for a week). Nothing here depends on position or on a default.
//
// The sub-line follows the 3f36f8d precedent — that card renders "(50% of 38)";
// here the percentage is the tile's headline, so the denominator goes beneath it.
const pctOf = (count, denom) => (denom > 0 ? Math.round((count / denom) * 100) : 0);
const ofUniqueVisitors = (count, denom) => `${count} of ${denom} unique visitors`;

// ── ENGAGED VISITORS AS A SHARE OF OPT-INS ──────────────────────────────────
// Ben: "if 50 people have opted in and there's only 25 engaged visitors in the
// masterclass then only 50% of people are engaging" — the figure he wants under
// each visitor count.
//
// THE DENOMINATOR IS ALWAYS THE OPT-IN FIGURE ON THE SAME TAB. Both tabs render
// this component and both read leads.totalLeads, which getAdminLeadsStats
// already fetched with the tab's own paidOnly flag — so Pages & Masterclass
// divides by its own total and Ad Metrics divides by the paid total, and in
// both cases the denominator is a tile Ben can see on the same screen. There is
// deliberately no third figure fetched for this.
//
// TWO HONEST LIMITS, BOTH HANDLED RATHER THAN HIDDEN:
//
//  1. IT COMPARES TWO DIFFERENT GRAINS. Engaged visitors are counted by DEVICE
//     (a browser-persisted visitor_id); opt-ins are counted by PERSON (one leads
//     row per email). One person on a phone and a laptop is two visitors and one
//     opt-in. So this is a guide, not an exact ratio, and the line under the
//     tiles says so.
//
//  2. IT CAN EXCEED 100%, and legitimately — a page can be reached from a direct
//     link without opting in first. Ben's live Funnel Overview already shows 71
//     masterclass arrivals against 54 opt-ins.
//     NOTE: the masterclass card's own badge still reads "Gated — everyone here
//     opted in first", which contradicts that. It is pre-existing and NOT
//     changed here; the wording above is deliberately page-agnostic so this
//     file does not assert both things on one screen. Flagged for Ben.
//     Rendering "131% of opt-ins" would read as a broken number, so anything
//     above 100 renders '>100%'. The two raw figures are both on screen, so
//     nothing is lost: Ben can see the visitor count and the opt-in count and
//     read the gap himself. Capping the LABEL, not the data.
//
// Zero opt-ins in the selected timeframe renders a dash — not 0%, which would
// read as "nobody engaged", and not a division error.
// The percentage on its own — split out (2026-09-17) so the funnel strip at the
// top of Ad Metrics can show the SAME value the cards show, rather than a second
// computation of it. shareOfOptins below is unchanged in what it returns.
const sharePct = (count, denom) => {
  const n = Number(count), d = Number(denom)
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return '—'
  const pct = (n / d) * 100
  if (pct > 100) return '>100%'
  // Exactly zero is '0%', not '0.0%' — the decimal is there to separate 6% from
  // 6.8% at low volumes, and there is nothing to separate at zero.
  if (pct === 0) return '0%'
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`
}
const shareOfOptins = (count, optIns) => `${sharePct(count, optIns)} of opt-ins`

// ── CTA CLICKERS AS A SHARE OF OPT-INS ──────────────────────────────────────
// The second line under each card's Clicked CTA tile. Ben wants the clicker
// count read against the list as well as against the page: "of the people who
// opted in, how many went on to click?"
//
// BUILT ON shareOfOptins, NOT BESIDE IT. The percentage phrase — its wording,
// its one-decimal rule, the dash at zero opt-ins and the '>100%' cap — is the
// exact string that helper returns, so this line and the ENGAGED VISITORS line
// on the same card can never disagree about how a share of opt-ins is written.
// Same denominator too: the caller passes leads.totalLeads, the Opt-ins tile on
// the same tab, exactly as the engaged line does.
//
// The bracket carries the two raw figures. The tile's headline is a share of
// unique visitors, so without this the clicker COUNT would appear only on the
// line above and the opt-in count only on another card — "(13 of 54)" puts
// both ends of this division on the line that performs it, following the
// 3f36f8d "(50% of 38)" precedent. When opt-ins are zero it prints "(13 of 0)"
// rather than hiding the count: the dash already says there is no rate, and
// the bracket says why.
//
// The count is CLICKERS — people — never raw clicks. Both callers pass a
// person-deduped figure (masterclass cta.clicked.count, sales
// checkout.ctaClickSessions), so one person clicking twice cannot move it.
const clickersShareOfOptins = (count, optIns) =>
  `${shareOfOptins(count, optIns)} (${Number(count) || 0} of ${Number(optIns) || 0})`

// ── THE FUNNEL IN ONE LINE — Ad Metrics only ────────────────────────────────
// Five PEOPLE counts, each read from the same field the card below reads, and
// each rate from 2 onwards a share of the step IMMEDIATELY BEFORE it — never of
// the top of the funnel — with an "of …" line saying so. Nothing is counted
// here: `val` is the card's field, `pct` is the card's helper, and the "of …"
// text is the one thing that makes a bare percentage unambiguous.
//
// A grid, not a flex row: five stacked figures do not fit side by side at a
// readable size on a 375px phone, and shrinking the text would make the "of …"
// line the first casualty. It wraps to three-across instead.
const FunnelStrip = ({ steps, loading, failed }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10,
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: '14px 16px', marginBottom: 14,
  }}>
    {steps.map(s => {
      const known = !loading && !failed && s.val !== null && s.val !== undefined;
      return (
        <div key={s.label} style={{ textAlign: 'center', minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.heading, lineHeight: 1.1 }}>
            {loading ? <span style={{ color: C.border }}>…</span>
              : known ? (s.display ?? Number(s.val).toLocaleString())
              : <span style={{ color: C.border }}>—</span>}
          </div>
          {/* A small second reading of the same figure, directly beneath it
              (today: Cost per opt-in's ≈ US$). Known figures only. */}
          {!loading && known && s.subDisplay && (
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginTop: 2 }}>{s.subDisplay}</div>
          )}
          <div style={{ fontSize: 10, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 3, lineHeight: 1.3 }}>
            {s.label}
          </div>
          {s.of && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 6 }}>
                {/* No number, no percentage — a rate over an unknown is a claim. */}
                {known && s.prevKnown ? s.pct : <span style={{ color: C.border }}>—</span>}
              </div>
              <div style={{ fontSize: 10.5, color: C.accent, lineHeight: 1.35 }}>of {s.of}</div>
            </>
          )}
          {/* A SECOND RATE beneath the first, same look and same unknown rule:
              the step's count against an earlier step (today: opt-ins). */}
          {(s.also || []).map(a => (
            <div key={a.of}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 6 }}>
                {known && a.prevKnown ? a.pct : <span style={{ color: C.border }}>—</span>}
              </div>
              <div style={{ fontSize: 10.5, color: C.accent, lineHeight: 1.35 }}>of {a.of}</div>
            </div>
          ))}
          {s.note && <div style={{ fontSize: 9.5, color: C.accent, opacity: 0.8, marginTop: 4 }}>{s.note}</div>}
          {/* Working lines under a computed figure (today: Cost per opt-in).
              Shown only once the figure is known, so no stale working sits
              under a "…" or "—". */}
          {known && (s.lines || []).map((l, i) => (
            <div key={i} style={{ fontSize: i === 0 ? 10.5 : 9.5, color: i === 0 ? C.text : C.accent, opacity: i === 0 ? 1 : 0.8, marginTop: i === 0 ? 6 : 2, lineHeight: 1.35 }}>{l}</div>
          ))}
        </div>
      );
    })}
  </div>
);

// ── THE STRIP'S "COST PER OPT-IN" STEP (Ad Metrics) ─────────────────────────
// leads = the tab's own /leads-stats response, requested with adSpend=1.
// val is the SPEND (so the step reads as known once spend has loaded, even with
// zero opt-ins); display is the cost, or "—" when there are no opt-ins to
// divide by. A spend that failed to load leaves val null → "—" and no working.
const SPEND_STALE_MS = 12 * 60 * 60 * 1000;
const aud = (n) => `A$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// ≈ US$ under the figure (Ben, 2026-09-30). NOT a live rate: the SAME
// display-only conversion the Revenue tab's USD view uses (revenueShared
// display → ÷ rates.outUsdAud, "A US$1 bill costs you" in the Cost Planner,
// saved value or its default). One rate for the whole admin, set in one place.
const usd = (n) => `≈ US$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function costPerOptInStep(leads, rates = DEFAULT_RATES, now = Date.now()) {
  const sp = leads?.adSpend;
  const optins = leads?.totalLeads;
  const ok = sp && typeof sp.spend === 'number' && typeof optins === 'number';
  const fetched = ok && sp.lastFetchedAt ? Date.parse(sp.lastFetchedAt) : NaN;
  const stale = Number.isFinite(fetched) && now - fetched > SPEND_STALE_MS;
  return {
    label: 'Cost per opt-in',
    val: ok ? sp.spend : null,
    display: ok ? (optins > 0 ? aud(sp.spend / optins) : '—') : undefined,
    subDisplay: ok && optins > 0 && Number(rates?.outUsdAud) > 0
      ? usd(displayCurrency(sp.spend / optins, 'USD', rates)) : undefined,
    lines: ok ? [
      `${aud(sp.spend)} spend ÷ ${optins.toLocaleString()} opt-in${optins === 1 ? '' : 's'}`,
      'all ad spend, incl. warm-up',
      sp.wholeDays ? 'spend counted by whole day' : null,
      optins > 0 && Number(rates?.outUsdAud) > 0 ? `US$ at A$${Number(rates.outUsdAud).toFixed(2)} per US$1 (Cost Planner rate)` : null,
      stale ? `spend last updated ${new Date(fetched).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : null,
    ].filter(Boolean) : [],
  };
}

// Inline stat tile (smaller than the shared StatTile, fits in 3-col grids)
const Tile = ({ label, val, sub, accent, onClick, open }) => (
  <div
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    style={{
      background: '#FDFAF8', borderRadius: 8, padding: '10px 8px',
      textAlign: 'center', border: `1px solid ${onClick && open ? C.heading : C.border}`,
      ...(onClick ? { cursor: 'pointer', userSelect: 'none' } : {}),
    }}
  >
    <div style={{ fontSize: 9, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
      {label}
      {onClick && (
        <span style={{ marginLeft: 4, fontSize: 8, display: 'inline-block', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      )}
    </div>
    <div style={{ fontSize: 20, fontWeight: 800, color: accent || C.heading }}>{val}</div>
    {sub && <div style={{ fontSize: 9, color: C.accent, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Horizontal progress bar with label + value. pct null = no base to divide by
// (nobody started the sales video in the window): a dash and an empty bar.
const MilestoneBar = ({ label, pct, count }) => (
  <div style={{ marginBottom: 7 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.accent, marginBottom: 3 }}>
      <span>{label}</span>
      <span>
        <strong style={{ color: C.heading }}>{pct == null ? '—' : `${pct}%`}</strong>
        <span style={{ color: C.border, margin: '0 3px' }}>·</span>
        <span style={{ color: C.accent }}>{count}</span>
      </span>
    </div>
    <div style={{ background: C.barTrack, borderRadius: 4, height: 6, overflow: 'hidden' }}>
      <div style={{
        width: `${pct == null ? 0 : Math.min(100, pct)}%`, height: '100%',
        background: `linear-gradient(90deg, ${C.chartBar}, ${C.cta})`,
        borderRadius: 4,
      }} />
    </div>
  </div>
);

// ── "UNDER 25% SEEN" — THE ONE BAND IN THE WATCH-DEPTH LIST (2026-10-06) ─────
// People who STARTED watching (the card's own definition: totalPlays) but saw
// less than a quarter. Every milestone count is taken from the same `plays`
// set totalPlays counts (routes/admin.js buildStats: m25 = plays.filter(seen
// >= 25%)), so 25% seen ⊆ started and under25 + p25 = started EXACTLY, for
// every period, filter, paid-only setting and video, exact or estimated alike.
// People who never started are not in it. No figure is computed anywhere new:
// it is the difference of two numbers the card already shows.
const under25Of = (v) => v.totalPlays - v.milestones.p25.count;
const Under25Note = ({ v }) => (
  <div style={{ fontSize: 9, color: C.accent, fontStyle: 'italic', lineHeight: 1.5, margin: '-2px 0 8px' }}>
    "Under 25% seen" is a band — started watching but saw less than a quarter; the rows
    below it count everyone who saw at least that much.
    {' '}{under25Of(v)} + {v.milestones.p25.count} = {v.totalPlays} started watching.
  </div>
);

// Skeleton shimmer for loading state
const Shimmer = ({ h = 14, w = '100%', mb = 8 }) => (
  <div style={{
    height: h, width: w, borderRadius: 6, marginBottom: mb,
    background: `linear-gradient(90deg, ${C.barTrack} 25%, #F8EDE4 50%, ${C.barTrack} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  }} />
);

// ── Page column card: accent top edge + badge + gating note ──────────────────
const PageColumn = ({ accent, badge, title, note, children }) => (
  <div style={{
    flex: 1, minWidth: 280, background: C.card,
    border: `1px solid ${C.border}`, borderTop: `3px solid ${accent}`,
    borderRadius: 12, padding: '18px 20px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
      <span style={{
        fontSize: 9, fontWeight: 800, color: '#FFF8F7', background: accent,
        borderRadius: 6, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {badge}
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.heading }}>{title}</span>
    </div>
    {note && <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginBottom: 12 }}>{note}</div>}
    {children}
  </div>
);

// ── MOBILE vs OTHER, all three pages together ────────────────────────────────
// TWO BUCKETS ONLY. Mobile = phones. Other = tablets, iPads, laptops, desktops.
// Ben's decision: what matters is small screen vs big screen, and a tablet
// behaves like a big screen. The classification itself lives server-side in
// backend/src/utils/deviceBucket.js — see that file for why it diverges from
// services/tags.js detectDevice() on iPads and on the bare Facebook-Android UA.
//
// WHY ALL THREE ON ONE CARD: a device split for a single page says almost
// nothing. The value is the comparison down the funnel — if phones are 94% of
// opt-in arrivals but 30% of sales-page arrivals, mobile visitors are falling
// out along the way. Stacked and left-aligned so that slope is the first thing
// read, without Ben assembling it in his head.
//
// Deliberately small: a supporting detail under the three page columns, not a
// section competing with the funnel figures above it.

// Below this many CLASSIFIED arrivals no percentage is drawn — just the counts.
// A "30% mobile" over 10 arrivals is one person either way, and reading it as a
// trend is a mistake this project has already made more than once. 30 is where
// a single visitor stops moving the figure by more than ~3 points.
const DEVICE_MIN_FOR_PCT = 30;

const DeviceRow = ({ accent, label, devices }) => {
  // No split in the response at all (older backend still deploying) — render
  // nothing for this page rather than an empty or zeroed row.
  if (!devices) return null;
  const { mobile = 0, other = 0, unknown = 0, unclassifiable = 0, total = 0 } = devices;
  const classified = mobile + other;
  const enough = classified >= DEVICE_MIN_FOR_PCT;
  const pct = classified > 0 ? Math.round((mobile / classified) * 100) : null;

  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 8, fontSize: 10, marginBottom: 3, flexWrap: 'wrap',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ width: 6, height: 6, borderRadius: 2, background: accent, flexShrink: 0 }} />
          <span style={{ color: C.heading, fontWeight: 700 }}>{label}</span>
        </span>
        {/* Percentage AND count, always — a bare percentage invites
            over-reading a small sample. Below the threshold the percentage is
            dropped entirely and only the counts remain. */}
        <span style={{ color: C.accent, whiteSpace: 'nowrap' }}>
          {enough && <strong style={{ color: C.heading, fontSize: 11 }}>{pct}% mobile</strong>}
          {enough && <span style={{ color: C.border, margin: '0 4px' }}>·</span>}
          <span>{mobile} mobile / {other} other</span>
        </span>
      </div>

      {/* The bar is what makes the drop-off across the three pages readable at
          a glance, so it is drawn even below the threshold — but faded, so a
          five-arrival page cannot look as settled as a five-hundred-arrival
          one. The number is suppressed; only the shape survives. */}
      {classified > 0 && (
        <div style={{
          display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden',
          background: C.barTrack, opacity: enough ? 1 : 0.4,
        }}>
          <div style={{ width: `${(mobile / classified) * 100}%`, background: accent }} />
          <div style={{ width: `${(other / classified) * 100}%`, background: C.barTrack }} />
        </div>
      )}

      {/* Honesty lines. Only rendered when they actually apply, so the card
          stays quiet when there is nothing to caveat. */}
      {!enough && (
        <div style={{ fontSize: 9.5, color: C.accent, fontStyle: 'italic', marginTop: 2 }}>
          {classified === 0
            ? 'No arrivals in this window.'
            : `Only ${classified} arrival${classified === 1 ? '' : 's'} — too few for a percentage.`}
        </div>
      )}
      {unclassifiable > 0 && (
        <div style={{ fontSize: 9.5, color: C.accent, fontStyle: 'italic', marginTop: 2 }}>
          {classified} of {total} loads have a device recorded; the other {unclassifiable} predate device tracking on this page.
        </div>
      )}
      {/* Never silently folded into a bucket. Zero today — no source that
          reaches these counts can arrive without a user-agent — but if that
          ever changes it says so here instead of quietly inflating Other. */}
      {unknown > 0 && (
        <div style={{ fontSize: 9.5, color: C.red, fontStyle: 'italic', marginTop: 2 }}>
          {unknown} arrival{unknown === 1 ? '' : 's'} with no device information — counted in neither bucket.
        </div>
      )}
    </div>
  );
};

const DeviceSplit = ({ optin, masterclass, ry }) => {
  // Nothing to show until at least one page reports a split.
  if (!optin && !masterclass && !ry) return null;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '12px 14px', marginBottom: 20,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
        Phone vs bigger screen
      </div>
      <div style={{ fontSize: 9.5, color: C.accent, marginBottom: 10, lineHeight: 1.5 }}>
        Share of arrivals to each page. <strong style={{ color: C.text }}>Other</strong> is
        everything that is not a phone — tablets, iPads, laptops, desktops. Same window and
        same filters as the figures above.
      </div>
      <DeviceRow accent={ACCENT.optin} label="Opt-in"      devices={optin} />
      <DeviceRow accent={ACCENT.mc}    label="Masterclass" devices={masterclass} />
      <DeviceRow accent={ACCENT.ry}    label="Sales page"  devices={ry} />
    </div>
  );
};

// ── Full-width collapsible detail row, colour-coded per page ─────────────────
// ── ONE PAGE AT A TIME, INSIDE A PANEL ──────────────────────────────────────
// Two sub-tabs, never both. Deliberately NOT two more collapsibles: these are
// the SAME question asked of two pages, and showing them stacked invites
// reading one page's bar against the other's when the denominators are
// unrelated. Masterclass first — it is where the journey starts.
const PageSubTabs = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
    {[['masterclass', 'Masterclass'], ['sales', 'Sales page']].map(([id, label]) => {
      const on = value === id;
      return (
        <button key={id} type="button" onClick={() => onChange(id)}
          style={{
            padding: '5px 12px', borderRadius: 16, fontSize: 11, fontWeight: 700, fontFamily: FONT,
            border: `1px solid ${on ? C.heading : C.border}`,
            background: on ? C.heading : C.card,
            color: on ? '#FFF8F7' : C.accent, cursor: 'pointer',
          }}>
          {label}
        </button>
      );
    })}
  </div>
);

// m:ss, never raw seconds. "642" is a number; "10:42" is a place in a video.
const mmss = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// The date line that sits under every one of these panels, full or empty.
// ALWAYS PRESENT, deliberately: it is the difference between "nobody clicked"
// and "we were not recording yet", and a panel that only explains itself when
// it is empty teaches the reader nothing when it fills.
const recordingSince = (iso) => {
  if (!iso) return 'Not recording yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Not recording yet';
  return `Recording since ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

// ── WHICH CTA THEY CLICKED, GROUPED BY THE PERMANENT TAG ────────────────────
// ONE COMPONENT FOR BOTH PAGES. Rows are keyed on data-cta (mc-01 …, ry-01 …),
// never on the wording, so renaming a button keeps its history in one row and
// two buttons that happen to share a name stay apart. The label is the button's
// CURRENT text, read from the most recent click; the tag sits beside it so a
// row can be traced back to a specific button on the page.
//
// UNTAGGED ROWS ARE KEPT AND SAID SO. Every click recorded before the tags
// shipped carries no tag. Those rows group under their own text and are
// labelled "untagged" — dropping them would lose real clicks, and folding them
// into a tagged row would claim they came from a button nobody can prove they
// came from.
//
// THE DENOMINATOR IS NAMED ON SCREEN, and it is the number of PEOPLE who
// clicked any CTA on that page in this window — computed from the same rows, so
// it cannot disagree with a figure computed elsewhere. Per-row "people" is
// distinct clickers of that button; summing them would double-count anyone who
// clicked two, which is why no total is printed.
//
// A WINDOW THAT OPENS BEFORE RECORDING BEGAN shows the date line and nothing
// else — a zero would read as "nobody clicked" when the truth is "we were not
// listening yet".
// One row of the breakdown. The depth chip is omitted rather than zeroed when
// the row has no depth — a row whose clicks all predate depths must not claim
// to sit at the top of the page.
//
// ZERO IS PRINTED, NOT HIDDEN. A button nobody has pressed shows "0 clicks ·
// 0 people" in the same place as every other count, its text a shade lighter,
// so a scan down the list reads straight to the buttons people act on.
const CtaRow = ({ row, note }) => {
  const zero = row.clicks === 0;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.barTrack}`, fontSize: 12 }}>
      <span style={{ fontWeight: 600, color: zero ? C.accent : C.text, minWidth: 0 }}>
        {row.label}
        {/* nowrap: at 375px "ry-08" was breaking after the hyphen and rendering
            as "ry-" / "08" on two lines. */}
        <span style={{ fontSize: 9, color: C.accent, fontWeight: 400, marginLeft: 6, whiteSpace: 'nowrap' }}>
          {row.untagged ? 'untagged' : row.tag}
        </span>
        {row.depth != null && (
          <span style={{ fontSize: 9, color: C.accent, fontWeight: 600, marginLeft: 6, background: C.barTrack, borderRadius: 8, padding: '1px 6px', whiteSpace: 'nowrap' }}>
            ~{row.depth}% down
          </span>
        )}
        {note && <span style={{ fontSize: 9, color: C.amber, fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>{note}</span>}
      </span>
      <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap' }}>
        <strong style={{ color: zero ? C.accent : C.heading }}>{row.clicks}</strong> {row.clicks === 1 ? 'click' : 'clicks'}
        <span style={{ margin: '0 4px', color: C.border }}>·</span>{row.people} {row.people === 1 ? 'person' : 'people'}
      </span>
    </div>
  );
};

// ── WHICH CTA THEY CLICKED ───────────────────────────────────────────────────
// THE SALES PAGE LISTS EVERY BUTTON, CLICKED OR NOT. `data.pageButtons` is the
// page's own list, read by the backend from the LIVE sales page
// (backend/src/services/salesPageCtas.js) — every data-cta button in page
// order with its fixed data-cta-depth. There is no second list to keep in
// step: reorder or add a button on the page and this follows. Clicks and people
// on each row are the endpoint's tagged figures, unchanged.
//
// WHEN THE PAGE CANNOT BE READ this falls back to what it always showed — the
// buttons that have been clicked — and says so, rather than presenting a
// partial list as complete. The masterclass side has no page list and always
// shows its clicked buttons.
//
// THE OLD UNTAGGED CLICKS ARE KEPT, NOT SHOWN BY DEFAULT. They cannot be placed
// on the page, so they sit behind one control that opens them. Never deleted,
// never merged into a tagged row.
//
// THE BOTTOM LINE NAMES WHAT IT COUNTS. Tagged clicks and people first; the
// untagged ones, when there are any, on their own line saying they are not in
// the first figure. People are never summed across the two — someone who
// clicked in both periods would be counted twice.
const CtaTagBreakdown = ({ data, failed, pageLabel, paidCaveat, periodFrom, paidOnly }) => {
  const [showOld, setShowOld] = useState(false);
  if (failed) return <div style={{ fontSize: 11, color: C.accent }}>Could not load CTA data.</div>;
  if (!data)  return <div style={{ fontSize: 11, color: C.accent }}>Loading…</div>;
  const pb       = data.pageButtons;
  const fullList = !!(pb && pb.ok);
  const listed   = fullList ? pb.rows.filter(r => r.onPage) : data.byTag.filter(r => !r.untagged);
  const offPage  = fullList ? pb.rows.filter(r => !r.onPage) : [];
  const untagged = data.byTag.filter(r => r.untagged);
  const anyDepth = listed.some(r => r.depth != null);
  const drift    = fullList ? (pb.drift || []) : [];
  const driftOf  = new Map(drift.map(d => [d.tag, d]));
  const taggedClicks   = data.taggedClicks   ?? data.totalClicks;
  const taggedClickers = data.taggedClickers ?? data.clickers;
  const oldClicks      = data.untaggedClicks ?? 0;
  const oldClickers    = data.untaggedClickers ?? 0;
  const readAt = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); };
  // ── THE TRUE TOTAL (2026-10-06) ──────────────────────────────────────────
  // The rows below cannot be added up for people (one person can click two
  // buttons), so the panel states the endpoint's own distinct-people and click
  // totals — tagged AND untagged, the same rows the card counts. On the sales
  // page this IS the card's "Clicked a CTA" (checkout.ctaClickSessions and the
  // summed ctaByText clicks), checked equal over every period tried. The
  // masterclass card counts from its session records since tracking began, and
  // button-level clicks were only recorded from data.firstSeen (26 Sept), and
  // this panel is not paid-filtered there — so when the period starts earlier,
  // or paid-only is on, the line says it will not match the card.
  const allClickers = data.clickers ?? 0;
  const allClicks   = data.totalClicks ?? 0;
  const isMc        = pageLabel === 'masterclass';
  const startsEarly = isMc && data.firstSeen && (!periodFrom || Date.parse(periodFrom) < Date.parse(data.firstSeen));
  const mcPaidGap   = isMc && paidOnly;
  const fmtDay = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <>
      <div style={{ fontSize: 12, color: C.text, marginBottom: 8, lineHeight: 1.5 }}>
        <strong style={{ color: C.heading }}>{allClickers.toLocaleString()} different {allClickers === 1 ? 'person' : 'people'} clicked</strong>
        <span style={{ margin: '0 5px', color: C.border }}>·</span>
        {allClicks.toLocaleString()} {allClicks === 1 ? 'click' : 'clicks'} in total
        {(startsEarly || mcPaidGap) && (
          <div style={{ fontSize: 10, color: C.amber, fontWeight: 600, marginTop: 2 }}>
            {startsEarly && <>Counts masterclass button clicks from {fmtDay(data.firstSeen)} only, when button-by-button recording began, so it won't match the card's Clicked CTA for this period.</>}
            {startsEarly && mcPaidGap && ' '}
            {mcPaidGap && <>Not filtered to paid traffic, so it won't match the card's Clicked CTA here.</>}
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: C.accent, marginBottom: 10, lineHeight: 1.5 }}>
        {fullList
          ? <>Every button on the sales page, top to bottom, whether or not anyone has clicked it. Grouped by each button's permanent tag, so buttons sharing a name stay apart.</>
          : <>Grouped by each button's permanent tag, so renaming a button keeps its history and buttons sharing a name stay apart.</>}
        {/* WHAT THE PERCENTAGE IS, IN ONE LINE. It is the button's own place on
            the page, not a share of anything, and it is approximate — the page
            is about three times taller on a desktop than on a phone. Said once,
            here, rather than repeated on fifteen rows. */}
        {anyDepth && <><br />The % is roughly how far down the page each button sits — its fixed place, not where anyone clicked from, and approximate since page height varies by device.</>}
        {paidCaveat && <><br />Not filtered to paid traffic — ad attribution is built from sales-page visitors, so it cannot be applied here.</>}
        {pb && pb.ok && pb.source === 'stale' && <><br />Button list as last read from the sales page at {readAt(pb.fetchedAt)} — it could not be read just now.</>}
        {pb && !pb.ok && <><br /><span style={{ color: C.amber, fontWeight: 600 }}>Could not read the sales page's list of buttons just now ({pb.error}) — showing only buttons that have been clicked.</span></>}
      </div>

      {/* ── A BUTTON NOT WHERE THE PAGE SAYS IT IS ──────────────────────────
          Each click records where the button really sat on that screen. If
          that has moved well away from the page's declared depth, the page
          has probably been rearranged without its depths being re-measured. */}
      {drift.length > 0 && (
        <div style={{ borderLeft: `3px solid ${C.amber}`, paddingLeft: 10, marginBottom: 10, fontSize: 10, color: C.amber, lineHeight: 1.5, fontWeight: 600 }}>
          {drift.map(d => (
            <div key={d.tag}>{d.tag} ("{d.label}") is declared at {d.declared}% but clicks place it at about {d.measured}% ({d.clicks} clicks).</div>
          ))}
          <div style={{ fontWeight: 400 }}>The sales page may have been rearranged without its depths being updated — re-measure the data-cta-depth values in the CTA DEPTH MAP.</div>
        </div>
      )}

      {listed.map(row => (
        <CtaRow key={row.tag} row={row} note={driftOf.has(row.tag) ? 'position changed?' : null} />
      ))}

      {/* A tag that has clicks but is no longer on the page — kept, so no
          recorded click ever vanishes from this panel. */}
      {offPage.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, marginBottom: 2 }}>Clicked, but no longer on the page</div>
          {offPage.map(row => <CtaRow key={row.tag} row={row} />)}
        </div>
      )}

      {/* ── HISTORICAL CLICKS THAT CANNOT BE PLACED — ONE CONTROL AWAY ────── */}
      {untagged.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <button type="button" onClick={() => setShowOld(v => !v)} aria-expanded={showOld}
            style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.heading }}>
              Before the buttons were tagged
              <span style={{ fontWeight: 400, color: C.accent }}> — {oldClicks} {oldClicks === 1 ? 'click' : 'clicks'} by {oldClickers} {oldClickers === 1 ? 'person' : 'people'}</span>
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, whiteSpace: 'nowrap' }}>{showOld ? 'Hide ▴' : 'Show ▾'}</span>
          </button>
          {showOld && (
            <>
              <div style={{ fontSize: 10, color: C.accent, margin: '4px 0 6px', lineHeight: 1.5 }}>
                Grouped by wording only — these clicks cannot be traced to a specific button or a place on the page.
              </div>
              {untagged.map(row => <CtaRow key={'u:' + row.label} row={row} />)}
            </>
          )}
        </div>
      )}

      {(listed.length > 0 || untagged.length > 0) && (
        <div style={{ fontSize: 10, color: C.accent, marginTop: 10, lineHeight: 1.5 }}>
          <strong style={{ color: C.heading }}>{taggedClickers} {taggedClickers === 1 ? 'person' : 'people'}</strong> clicked a tagged {pageLabel} button in this period ({taggedClicks} {taggedClicks === 1 ? 'click' : 'clicks'}).
          {oldClicks > 0 && <><br />Not included in that: {oldClicks} {oldClicks === 1 ? 'click' : 'clicks'} by {oldClickers} {oldClickers === 1 ? 'person' : 'people'} from before the buttons were tagged.</>}
        </div>
      )}
      <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: 10 }}>
        {recordingSince(data.firstSeen)}
      </div>
    </>
  );
};

// ── ONE MARK PER CLICK, AGAINST THE VIDEO'S OWN LENGTH ──────────────────────
// NOT A HISTOGRAM. Bars would answer "how many clicked in minute 7", which is
// a question nobody asked; the point here is seeing where the clusters fall,
// so every click keeps its own mark.
//
// OVERLAP IS HANDLED BY STACKING, NOT BY HIDING. Clicks landing within the
// same 1% of the video are stacked upward in the order they arrive, so two
// clicks a second apart are two visible marks rather than one darker one.
// Translucent fill on top of that, so a genuine pile-up still reads darker.
//
// The axis runs 0 → the video's own length, which the endpoint reads from the
// player's own total_duration_secs rather than this file hardcoding it.
const VideoClickPlot = ({ clicks, durationSecs }) => {
  const dur = durationSecs && durationSecs > 0 ? durationSecs : null;
  const H = 120, DOT = 7, LANE = 9;

  // Bucket by percentage-of-video so stacking is resolution-independent.
  const lanes = new Map();
  const marks = (clicks || []).map((secs) => {
    const pct = dur ? Math.min(100, Math.max(0, (secs / dur) * 100)) : 0;
    const key = Math.round(pct);
    const n = lanes.get(key) || 0;
    lanes.set(key, n + 1);
    return { secs, pct, lane: n };
  });
  const maxLane = marks.reduce((m, k) => Math.max(m, k.lane), 0);
  const plotH = Math.max(H, (maxLane + 1) * LANE + 24);
  const ticks = dur ? [0, 0.25, 0.5, 0.75, 1] : [];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ position: 'relative', height: plotH, background: '#FDFAF8', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {/* Quarter gridlines, so a cluster can be placed without counting pixels */}
        {ticks.slice(1, -1).map(t => (
          <div key={t} style={{ position: 'absolute', left: `${t * 100}%`, top: 0, bottom: 0, width: 1, background: C.barTrack }} />
        ))}
        {/* THE 3-MINUTE LINE is drawn by the caller via `children`-free props;
            kept out of here so this component stays about marks only. */}
        {/* THE MARKS SIT ON AN INSET TRACK, not on the box itself. Positioning
            a dot at `calc(100% - half a dot)` of the full width pushes its
            right edge past the border and the box's overflow:hidden clips it —
            so a click in the last seconds of the video, which is exactly the
            kind you want to see, would render as a half-circle. The track is
            inset by half a dot at each end, so 0% and 100% both land fully
            inside. Measured at 375px: without this the plot's scrollWidth ran
            2px past its own width. */}
        <div style={{ position: 'absolute', left: DOT / 2, right: DOT / 2, top: 0, bottom: 0 }}>
          {marks.map((m, i) => (
            <div key={i} title={mmss(m.secs)} style={{
              position: 'absolute',
              left: `calc(${m.pct}% - ${DOT / 2}px)`,
              bottom: 18 + m.lane * LANE,
              width: DOT, height: DOT, borderRadius: '50%',
              background: 'rgba(184, 52, 24, 0.55)',
              border: '1px solid rgba(184, 52, 24, 0.85)',
            }} />
          ))}
        </div>
        {/* Axis labels INSIDE the plot box, so the panel can never be wider
            than its container and cause a sideways scroll at 375px. */}
        {ticks.map(t => (
          <span key={'l' + t} style={{
            position: 'absolute', bottom: 3,
            left: t === 0 ? 4 : t === 1 ? undefined : `${t * 100}%`,
            right: t === 1 ? 4 : undefined,
            transform: t > 0 && t < 1 ? 'translateX(-50%)' : undefined,
            fontSize: 9, color: C.accent, whiteSpace: 'nowrap',
          }}>
            {dur ? mmss(dur * t) : ''}
          </span>
        ))}
      </div>
    </div>
  );
};

const DetailRow = ({ accent, badge, title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${accent}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* OPTIONAL. A section covering BOTH pages has no single badge to
              wear — "SALES" on a panel with a Masterclass tab inside it is
              simply wrong. Every existing caller still passes one and renders
              byte-identically; only an omitted badge changes anything. */}
          {badge && (
            <span style={{
              fontSize: 8, fontWeight: 800, color: '#FFF8F7', background: accent,
              borderRadius: 5, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {badge}
            </span>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: C.heading }}>{title}</span>
        </span>
        <span style={{ fontSize: 12, color: C.accent, transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>
      {open && <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 16px' }}>{children}</div>}
    </div>
  );
};

// Raw reason/code strings are for machines. Ben gets plain English.
const failureReasonLabel = (f) => {
  if (f.reason === 'card_error') {
    if (f.code === 'card_declined')      return 'card declined';
    if (f.code === 'expired_card')       return 'card expired';
    if (f.code === 'incorrect_cvc')      return 'wrong security code';
    if (f.code === 'insufficient_funds') return 'insufficient funds';
    if (f.code === 'processing_error')   return 'card processing error';
    return 'card problem';
  }
  if (f.reason === 'backend') return 'payment error';
  if (f.reason === 'network') return 'connection dropped';
  return 'unknown problem';
};

// ── Pre-checkout interstitial drop-off ───────────────────────────────────────
// Opens beneath the "Clicked a CTA" tile. Filtered by the SAME timeframe and
// the SAME paid resolver as that tile — see the /interstitial-funnel endpoint
// for why ry_interstitial_funnel is deliberately not read directly here.

// One step. The percentage is ALWAYS of a NAMED parent, printed on the row.
// A bare "% of the row above" reads as nonsense on the last two rows — "Bought"
// as a share of "Left without continuing" would say people left and then
// bought — so each row names its own denominator instead.
// A zero parent prints NO percentage: "0% of saw the popup" is noise, not data.
const StepRow = ({ label, note, n, parent, parentLabel, strong }) => {
  const pct = (parent === null || parent === undefined || parent === 0) ? null : pctOf(n, parent);
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 10, padding: '6px 0', borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: 11, color: strong ? C.heading : C.text, fontWeight: strong ? 700 : 500 }}>
        {label}
        {note && <span style={{ display: 'block', fontSize: 9, color: C.accent, marginTop: 1 }}>{note}</span>}
      </span>
      <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <strong style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>{(n ?? 0).toLocaleString()}</strong>
        {pct !== null && (
          <span style={{ fontSize: 9.5, color: C.accent, marginLeft: 6 }}>{pct}% of {parentLabel}</span>
        )}
      </span>
    </div>
  );
};

const InterstitialPanel = ({ data, failed }) => {
  const box = { borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 12 };

  if (failed) {
    return (
      <div style={{ ...box, fontSize: 10.5, color: C.accent, lineHeight: 1.55 }}>
        Couldn’t load the interstitial figures — the request didn’t come back. Every other
        figure on this page is unaffected; reload to try again.
      </div>
    );
  }
  if (!data) return <div style={{ ...box, fontSize: 10.5, color: C.accent }}>Loading…</div>;

  const s = data.steps;
  const c = data.comparison;
  const sawPct    = pctOf(c.saw.bought, c.saw.visitors);
  const notSawPct = pctOf(c.notSaw.bought, c.notSaw.visitors);

  // NO CONTROL GROUP. True once the split test is switched off and the popup
  // goes to everyone, and also true before any control traffic has arrived.
  // Derived from the data rather than from the page's config, because the
  // config lives in the sales page and this is the portal — and because the
  // sentence it produces ("nobody in this window skipped the popup") is true
  // either way, which a claim about the config would not be.
  //
  // Without this the panel prints "Didn't see it 0% bought (0 of 0)" and a
  // "too early, needs 30 in each group" line that can never resolve.
  const noControl = c.notSaw.visitors === 0;

  return (
    <div style={box}>

      {/* THE COMPARISON, PINNED AT THE TOP. This is the only number on the
          panel that answers "is the popup worth having", so it does not sit
          underneath eight rows of drop-off. */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
        <div style={{ fontSize: 9, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Did the popup help?
        </div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.text }}>
            Saw the popup{' '}
            <strong style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>{sawPct}%</strong>
            <span style={{ color: C.accent, fontSize: 10 }}> bought ({c.saw.bought} of {c.saw.visitors})</span>
          </span>
          {!noControl && (
            <span style={{ fontSize: 11, color: C.text }}>
              Didn’t see it{' '}
              <strong style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>{notSawPct}%</strong>
              <span style={{ color: C.accent, fontSize: 10 }}> bought ({c.notSaw.bought} of {c.notSaw.visitors})</span>
            </span>
          )}
          {!noControl && c.enoughData && (
            <span style={{ fontSize: 11, color: sawPct >= notSawPct ? C.green : C.amber, fontWeight: 700 }}>
              {sawPct >= notSawPct ? '+' : ''}{sawPct - notSawPct} percentage points
            </span>
          )}
        </div>

        {/* Asked for explicitly: be TOLD it is too early rather than misread a
            confident-looking percentage built on a handful of people. Worded
            for whichever number is actually on screen — a "30 in each group"
            line would be nonsense when there is only one group. */}
        {noControl
          ? (c.saw.visitors < c.minPerArm && (
              <div style={{ fontSize: 10, color: C.amber, marginTop: 7, lineHeight: 1.5, fontWeight: 600 }}>
                Too early to read this. One purchase moves it by tens of points. Needs at
                least {c.minPerArm} people — there {c.saw.visitors === 1 ? 'is' : 'are'}{' '}
                {c.saw.visitors} so far.
              </div>
            ))
          : (!c.enoughData && (
              <div style={{ fontSize: 10, color: C.amber, marginTop: 7, lineHeight: 1.5, fontWeight: 600 }}>
                Too early to read this. One purchase either way moves these numbers by tens of
                points. Needs at least {c.minPerArm} people in each group — there are{' '}
                {c.saw.visitors} and {c.notSaw.visitors} so far.
              </div>
            ))}

        <div style={{ fontSize: 9.5, color: C.accent, marginTop: 6, lineHeight: 1.5 }}>
          {/* Each branch is a complete sentence, and the cart-abandonment note is
              its own sentence after it — an em-dash clause tacked onto the
              no-control text read as ". — 1 person who...". */}
          {noControl
            ? <>Nobody in this window skipped the popup, so there is no control to compare
              against. Judge it against the period <strong style={{ color: C.text, fontWeight: 700 }}>before
              2 September 2026</strong>, when the popup did not exist.</>
            : <>A coin-flip decides who sees the popup. This compares those two halves only.</>}
          {c.excludedDirect > 0 && <> {c.excludedDirect} {c.excludedDirect === 1 ? 'person' : 'people'} who
          arrived straight from a cart-abandonment email {c.excludedDirect === 1 ? 'is' : 'are'} left out of
          it, because they had already decided once.</>}
        </div>
      </div>

      {s.clicked === 0 && (
        <div style={{ fontSize: 10.5, color: C.accent, marginBottom: 10, lineHeight: 1.55 }}>
          Nobody has clicked a buy button in this window yet. The popup went live on
          2 September 2026, so these fill in as traffic comes through — the zeros below are
          real, not missing data.
        </div>
      )}

      <StepRow label="Clicked a buy button" n={s.clicked} parent={null} strong />

      {/* EVERYTHING IN THIS BLOCK APPLIES TO HALF THE PEOPLE ONLY. Indented,
          left-barred and captioned, because in a month these rows sitting flush
          with the other two is exactly how they get read as shares of everyone. */}
      <div style={{ borderLeft: `3px solid ${C.amber}`, paddingLeft: 10, margin: '8px 0 8px 2px' }}>
        <div style={{ fontSize: 8.5, fontWeight: 800, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 0 2px' }}>
          {noControl ? 'Everyone who saw the popup' : 'Only the half who see the popup'} · {s.shown} of {s.clicked} clickers
        </div>
        <StepRow label="Saw the “Hold up.” popup"   n={s.shown}         parent={s.clicked}   parentLabel="clicked a buy button" />
        <StepRow label="Clicked “That’s me”"        n={s.accepted}      parent={s.shown}     parentLabel="saw the popup" />
        <StepRow label="Clicked “Take me back”"     n={s.dismissed}     parent={s.shown}     parentLabel="saw the popup" />
        <StepRow label="Saw the second-chance note" n={s.sheetShown}    parent={s.dismissed} parentLabel="clicked “Take me back”" />
        <StepRow label="Clicked “Alright. Screw it. Let’s do this!”" n={s.sheetAccepted} parent={s.sheetShown} parentLabel="saw the note" />
        <StepRow label="Left without continuing" note="closed it, clicked away, or did nothing"
                 n={s.leftWithout} parent={s.shown} parentLabel="saw the popup" />
      </div>

      <StepRow label="Bought" n={s.bought} parent={s.clicked} parentLabel="clicked a buy button" strong />

      {/* Asked for in plain English, not just a source label. */}
      <div style={{ fontSize: 9.5, color: C.accent, marginTop: 9, lineHeight: 1.6 }}>
        <strong style={{ color: C.text, fontWeight: 700 }}>Why “Bought” can differ from the sales tile above.</strong>{' '}
        That tile counts everyone who ended up enrolled, however they got there. This row counts
        only the people we could follow all the way back to a click on this page. So someone who
        clicked on their phone and paid later on a laptop, or cleared their cookies, or came
        straight in from an email link, is counted up there but not down here. The tile above is
        the right number for “how many sales”; this row is the right number for “what this popup
        did”.
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

// `paidOnly` renders this SAME component as the Ad Metrics tab: identical
// layout, every figure filtered to paid traffic by the backend. One component,
// one set of tiles — a forked near-duplicate is how the two views would drift.
//
// `onTimeframeChange`, when passed, fires with {range, from, to} whenever the
// resolved timeframe changes. AdminAdMetrics uses it to keep its own ad-spend
// section (which lives outside this component) in step with the same
// timeframe — this component still owns the state itself, so nothing about
// how Pages & Masterclass uses it (no callback passed) changes.
// ── ONE SALES VIDEO'S WATCH FIGURES ──────────────────────────────────────────
// The sales card's video block, moved here unchanged so it can be drawn once per
// video: since the switch went, a range that spans the change of sales video
// shows one of these for each, each against its own part of the range (uv =
// that part's unique visitors, as the old switch divided by) and its own
// length. `label` names the video and its dates when there is more than one.
//
// THE BARS ARE A SHARE OF THOSE WHO STARTED WATCHING (2026-10-21). The video is
// click-to-play: people who never press play are reached by the page's words,
// not the video, so the bars judge the video on the people who chose it —
// "of those who pressed play, how far did they get". The pressed-play figure
// itself (how inviting the video is) stays a share of unique visitors, in the
// note. Started = ry.totalPlays, the same set every milestone count is taken
// from, so no bar can pass 100%. Nobody started → dashes, never 0% or NaN.
// The masterclass block is NOT changed: it autoplays, so starting is not a
// choice there. AdminCompare.jsx videoRate copies this rule — change both.
const SalesVideoBlock = ({ ry, uv, label }) => {
  const salesUniqueVisitors = uv;
  if (!ry) {
    return <div style={{ fontSize: 11, color: C.accent, marginBottom: 10 }}>{label ? `${label} — ` : ''}No sales-page video data yet</div>;
  }
  const started = ry.totalPlays;
  const ofStarted = (count) => (started > 0 ? pctOf(count, started) : null);
  return (
    <div style={{ marginBottom: label ? 16 : 0 }}>
      {label && (
        <div style={{ fontSize: 10.5, fontWeight: 800, color: C.red, marginBottom: 6 }}>{label}</div>
      )}
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                  {/* ── "100% PLAY RATE" HAD TO GO ───────────────────────────
                      ry.playRate is plays ÷ ENGAGED visitors, and on a video
                      page engagement is very nearly "pressed play" — so it
                      trends to 100% by construction and read exactly 100% here
                      (7 of 7). The endpoint says as much where it is computed.

                      Left above bars that now divide by unique visitors it
                      became a flat contradiction: "100% play rate" sitting on
                      top of "25% watched — 32% of 19". Both cannot be true of
                      the same page. So the heading names the bars' denominator,
                      exactly like the masterclass heading beside it, and the
                      real play rate — 7 of 19, 37% — moves into the note below
                      where it is stated against a denominator that means
                      something. The plays COUNT is not lost; it is in that note. */}
                    <div style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Sales-page video — of {started} who started watching
                    </div>
                    {/* Same reasoning as the masterclass drop-off link: a
                        separate view that states its own denominator on its own
                        face. Untouched. */}
                    <DropOffLink
                      title={label ? `Sales-page video (${label}) — where people drop off` : 'Sales-page video — where people drop off'}
                      curve={ry.seenCurve}
                      landed={salesUniqueVisitors}
                      startedNote="Started = pressed play (this video is click-to-play)."
                    />
                  </div>
                  {/* The click-to-play note stays — it is still true and still
                      the reason this video's numbers mean something the
                      masterclass's do not — but it now carries the play rate it
                      used to sit underneath, against unique visitors. */}
                  <div style={{ fontSize: 9, color: C.accent, fontStyle: 'italic', marginBottom: 8 }}>
                    Click-to-play — no autoplay, so pressing play is a real choice.
                    {' '}{ry.totalPlays} of {salesUniqueVisitors} unique visitors pressed play
                    ({pctOf(ry.totalPlays, salesUniqueVisitors)}%).
                    {started > 0
                      ? <> The bars below are a share of the {started} who started watching: of the people who chose to watch, how far they got.</>
                      : <> Nobody started watching in this period, so the bars have nothing to measure.</>}
                  </div>
                  {/* ⚠ THE ONLY CROSS-TABLE DIVISION ON EITHER CARD — since
                      2026-10-21 only the pressed-play figure in the note above;
                      the bars divide by started (same table, can't pass 100%).
                      History kept below, stated plainly rather than hidden. The counts come from
                      ry_video_sessions; salesUniqueVisitors comes from
                      funnel_events. On the masterclass card both sides come from
                      one table, so numerator ⊆ denominator is guaranteed; here
                      it is not, and the two populations really do differ —
                      measured 2026-09-02 09:07Z on all traffic,
                      ry.uniqueVisitorsAll was 36 against funnel.uniqueViewers 35,
                      because ry_video_sessions carries no user_agent and so is
                      never bot-filtered.

                      funnel.uniqueViewers is still the right divisor: it is the
                      "Unique visitors" tile on this very card, it is what every
                      other percentage here divides by (3c91cea), and a
                      denominator Ben cannot see on screen is the failure this
                      whole change exists to remove. The alternative,
                      ry.uniqueVisitorsAll, is safer arithmetically and invisible
                      to him.

                      In practice the gap cannot bite: a bot does not watch a
                      quarter of a video, and the live margin is 7 against 35.
                      No cap is applied — if this ever did print above 100% that
                      would be real news about the data, and hiding it behind a
                      Math.min would be the worse failure. */}
                  {/* THE SEEN-TIME MEASURE (2026-09-29): the share of the video
                      actually seen, exact from the switch, estimated before it. */}
                  <MilestoneBar label="Under 25% seen" pct={ofStarted(under25Of(ry))} count={under25Of(ry)} />
                  <MilestoneBar label="25% seen" pct={ofStarted(ry.milestones.p25.count)} count={ry.milestones.p25.count} />
                  <MilestoneBar label="50% seen" pct={ofStarted(ry.milestones.p50.count)} count={ry.milestones.p50.count} />
                  <MilestoneBar label="75% seen" pct={ofStarted(ry.milestones.p75.count)} count={ry.milestones.p75.count} />
                  <MilestoneBar label="90% seen" pct={ofStarted(ry.milestones.p90.count)} count={ry.milestones.p90.count} />
                  <MilestoneBar label={ry.measure?.lastMilestone ?? 'Reached end'} pct={ofStarted(ry.milestones.end.count)} count={ry.milestones.end.count} />
                  <Under25Note v={ry} />
                  <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.accent }}>
                    Avg time seen: <strong style={{ color: C.heading }}>{fmtTime(ry.avgSecondsWatched)}</strong>
                    {ry.avgDurationSecs > 0 && <span style={{ marginLeft: 8 }}>· Video length: <strong style={{ color: C.heading }}>{fmtTime(ry.avgDurationSecs)}</strong></span>}
                  </div>
                </>
    </div>
  );
};

export const AdminPages = ({ paidOnly = false, onTimeframeChange }) => {
  // Opens on the saved "Opt-in Copy Change" marker when it exists (adminShared
  // useTimeframe); every fetch below waits for tfReady so nothing loads twice.
  const { range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, from, to, ready: tfReady } =
    useTimeframe('all', { preferMarkerName: DEFAULT_MARKER_NAME });

  const [wistia,  setWistia]  = useState(null);
  const [leads,   setLeads]   = useState(null);
  const [funnel,  setFunnel]  = useState(null);
  const [sales,   setSales]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // The interstitial panel under the "Clicked a CTA" tile. Fetched SEPARATELY
  // from the Promise.all above on purpose: this is one collapsed panel, and a
  // failure here must not blank the whole page the way a rejected Promise.all
  // would. Same pattern the Ad Metrics tab uses for its spend section.
  const [ctaOpen, setCtaOpen] = useState(false);
  const [interstitial, setInterstitial] = useState(null);
  const [interstitialFailed, setInterstitialFailed] = useState(false);

  // Which CTA / where in the video — BOTH pages, one request, fetched on the
  // same separate-from-Promise.all principle as the interstitial panel above:
  // it feeds two collapsed sections and a failure must not blank the page.
  const [ctaDetail, setCtaDetail] = useState(null);
  const [ctaDetailFailed, setCtaDetailFailed] = useState(false);

  // ── THE SALES VIDEO'S WATCH FIGURES, ONE BLOCK PER VIDEO ──────────────────
  // There is no sales video switch any more: every sales page figure follows
  // this tab's own range. The one exception is the video's WATCH figures —
  // v1 and v2 are different lengths and must never be blended — so each video
  // whose live period overlaps the range gets its own block, counted over its
  // own part of the range from its own records (salesVideoPeriods.js): exactly
  // what the old switch showed for that video over the same dates.
  const vw = useSalesVideoWindows(from, to);
  const [videoBlocks, setVideoBlocks] = useState(null);   // null = loading
  // Sub-tab state lives here, one per section, so each remembers its own
  // choice while the panel is open and both default to Masterclass.
  const [whichCtaPage, setWhichCtaPage] = useState('masterclass');
  const [videoCtaPage, setVideoCtaPage] = useState('masterclass');

  // Ad Metrics only: the Cost Planner's saved exchange rates for the strip's
  // "≈ US$" line — merged over the defaults exactly as CostPlanner.jsx does.
  // A failed read keeps the defaults; nothing else on the tab waits for it.
  const [fxRates, setFxRates] = useState(DEFAULT_RATES);
  useEffect(() => {
    if (!paidOnly) return;
    getBusinessPlan()
      .then(({ data }) => { if (data?.rates) setFxRates({ ...DEFAULT_RATES, ...data.rates }); })
      .catch(() => {});
  }, [paidOnly]);

  useEffect(() => {
    if (!tfReady) return;
    onTimeframeChange?.({ range, from, to });
    // onTimeframeChange intentionally excluded — AdminAdMetrics passes a new
    // function reference every render, which would otherwise fire this every
    // render rather than only when the resolved timeframe actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, from, to, tfReady]);

  useEffect(() => {
    if (!tfReady) return;
    setLoading(true);
    setError('');
    Promise.all([
      getAdminWistiaStats(from, to, paidOnly),
      // Ad Metrics asks for the range's ad spend alongside the opt-ins, for the
      // strip's "Cost per opt-in". Only THIS fetch (the tab's own range) asks —
      // never the sales-video-period fetch below — so the figure does not
      // follow the sales video switch.
      getAdminLeadsStats(from, to, paidOnly, paidOnly ? { adSpend: 1 } : {}),
      getAdminFunnelPageStats(from, to, paidOnly),
      getAdminRySalesStats(from, to, paidOnly),
    ])
      .then(([wRes, lRes, fRes, sRes]) => {
        setWistia(wRes.data);
        setLeads(lRes.data);
        setFunnel(fRes.data);
        setSales(sRes.data);
      })
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load page data'))
      .finally(() => setLoading(false));
  }, [from, to, paidOnly, tfReady]);

  // The interstitial panel under the sales card's "Clicked a CTA" tile — the
  // tab's own range, like every other sales page figure.
  useEffect(() => {
    if (!tfReady) return;
    setInterstitial(null); setInterstitialFailed(false);
    getAdminInterstitialFunnel(from, to, paidOnly)
      .then(res => setInterstitial(res.data))
      .catch(() => setInterstitialFailed(true));
  }, [from, to, paidOnly, tfReady]);

  // The video's watch figures and its "where in the video" clicks, per video:
  // for each video's window, the SAME three requests the old switch sent for it.
  useEffect(() => {
    if (!tfReady || !vw.key) return undefined;
    let active = true;
    setVideoBlocks(null);
    Promise.all(vw.windows.map(async ({ video, win, label }) => {
      const extra  = salesExtra(video.id, win);
      const extraV = salesExtra(video.id, win, { withVideo: true });
      const [w, f, c] = await Promise.allSettled([
        getAdminWistiaStats(win.from, win.to, paidOnly, extraV),
        getAdminFunnelPageStats(win.from, win.to, paidOnly, extra),
        getAdminCtaDetail(win.from, win.to, paidOnly, extraV),
      ]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : null);
      return {
        video, win, label,
        ry: val(w)?.ryLanding ?? null,
        uniqueViewers: val(f)?.uniqueViewers ?? 0,
        cta: val(c)?.sales ?? null,
        failed: [w, f, c].some(r => r.status === 'rejected'),
      };
    })).then(list => { if (active) setVideoBlocks(list); });
    return () => { active = false; };
  }, [vw.key, paidOnly, tfReady]);

  // Same timeframe and same paid flag as every other figure on this tab, so
  // the panels below can never be filtered differently from the tiles above.
  useEffect(() => {
    if (!tfReady) return;
    setCtaDetail(null);
    setCtaDetailFailed(false);
    getAdminCtaDetail(from, to, paidOnly)
      .then(res => setCtaDetail(res.data))
      .catch(() => setCtaDetailFailed(true));
  }, [from, to, paidOnly, tfReady]);

  // One page's "where in the video" plot and its counts (the masterclass once;
  // the sales page once per video, each against that video's own length).
  const renderVideoClicks = (d, isMc, caption) => {
          return (
            <>
              {/* THE THREE-MINUTE FLOOR, said before the plot rather than after
                  it. The masterclass CTAs do not exist on the page until three
                  minutes of watch time (or the video ending), so the empty
                  stretch on the left is the page working as designed, not
                  people declining to click. */}
              {isMc && (
                <div style={{ fontSize: 10, color: C.accent, marginBottom: 10, lineHeight: 1.5 }}>
                  The masterclass CTAs stay hidden until 3:00 of watch time (or the video ends), so nothing can appear before 3:00.
                  {paidOnly && <><br />Not filtered to paid traffic — ad attribution is built from sales-page visitors.</>}
                </div>
              )}
              <VideoClickPlot clicks={d.clicks} durationSecs={d.videoDurationSecs} />
              <div style={{ fontSize: 10, color: C.accent, marginTop: 8, lineHeight: 1.6 }}>
                {/* HOW MANY, AND OVER WHAT — stated under the plot, because a
                    scatter with no count is a shape without a size. */}
                <strong style={{ color: C.heading }}>{d.clicks.length}</strong>
                {d.clicks.length === 1 ? ' click' : ' clicks'} plotted · {caption}
                {d.videoDurationSecs ? <> · video runs {mmss(d.videoDurationSecs)}</> : null}
                {/* NEVER SILENTLY DROPPED, AND NEVER EXPLAINED AWAY. A click
                    with no video position cannot go on a time axis, so it is
                    counted here instead.
                    WHY THE FIELD IS ABSENT IS NOT STATED, because the row does
                    not say. It could be a click that predates the field
                    existing, or one where the player could not be read — and
                    this line used to assert "the video had not started", which
                    is a claim the data does not support. It now reports the
                    absence and the date the field began, and stops there. */}
                {d.noPosition > 0 && (
                  <><br /><strong style={{ color: C.heading }}>{d.noPosition}</strong>
                    {d.noPosition === 1 ? ' click has' : ' clicks have'} no video position recorded, so {d.noPosition === 1 ? 'it is' : 'they are'} not plotted.
                    {d.positionsFirstSeen && <> Video position has only been recorded since {new Date(d.positionsFirstSeen).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}.</>}</>
                )}
              </div>
              <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: 8 }}>
                {recordingSince(d.positionsFirstSeen)}
              </div>
            </>
          );
  };

  const mc = wistia?.masterclass;
  // The sales card reads the tab's OWN range data (the switch that narrowed it
  // is gone). The s* names are kept so the card's markup reads as before.
  const sFunnel = funnel, sLeads = leads, sSales = sales;
  const sLoading = loading, sError = error;
  const sCtaDetail = ctaDetail, sCtaDetailFailed = ctaDetailFailed;

  // Format daily opt-in dates for chart labels (backend sends 30 days; show last 7)
  const chartData = leads?.dailyOptins
    ? leads.dailyOptins.slice(-7).map(d => ({
        count: d.count,
        date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      }))
    : [];

  // Checkout drop-off, in funnel order. `people` is the session-deduplicated
  // figure (these measure people, not clicks); `raw` is shown only where it
  // differs — checkout_opened in particular fires once per modal open.
  // checkout_open_failed appears ONLY when non-zero: a permanent zero row is
  // noise, but a non-zero one means people literally could not buy.
  const ck = sFunnel?.checkout;

  // ── CAN THIS WINDOW ANSWER THE STEP-1 -> STEP-2 QUESTION AT ALL? ───────────
  // checkout_details_opened only exists from the moment the two-step cart went
  // live; before that the cart had no second step to reach. A window opening
  // earlier therefore holds checkout opens that COULD NOT have produced one, so
  // the percentage would describe a collapse that never happened. The row
  // prints a dash and says why instead.
  //
  // EPOCH MILLISECONDS, never a string compare: `from` is 'YYYY-MM-DD' for the
  // day presets, a full ISO instant for "Last 24 hours", and undefined for
  // "All time" — three shapes that do not sort against each other lexically.
  // undefined means no lower bound at all, which is by definition earlier than
  // the event, so All time takes the dash.
  const detailsFirstMs  = ck?.detailsOpenedFirstSeen ? Date.parse(ck.detailsOpenedFirstSeen) : NaN;
  const windowStartMs   = from ? Date.parse(from) : NaN;
  const detailsTooEarly = Number.isNaN(detailsFirstMs) || Number.isNaN(windowStartMs)
                          || windowStartMs < detailsFirstMs;
  const detailsSinceText = Number.isNaN(detailsFirstMs)
    ? 'Not recorded yet'
    : `Only recorded from ${new Date(detailsFirstMs).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;

  const checkoutFunnelRows = ck ? [
    { label: 'Clicked a CTA',     people: ck.ctaClickSessions,        raw: ck.ctaClicksRaw, rawNoun: 'clicks' },
    { label: 'Checkout opened',   people: ck.openedSessions,          raw: ck.openedRaw,    rawNoun: 'opens'  },
    // The two-step cart's step 2. Its denominator is NAMED on the row and is
    // the checkout-opened figure printed directly above it — same window, same
    // tab, same paid filter. No new basis is introduced.
    { label: 'Reached the payment form',
      people:    ck.detailsOpenedSessions,
      raw:       null,
      pctDenom:  ck.openedSessions,
      pctNoun:   'checkout opens',
      unavailable: detailsTooEarly,
      unavailableNote: detailsSinceText },
    ...(ck.openFailedSessions > 0
      ? [{ label: "Couldn't open checkout", people: ck.openFailedSessions, raw: null }]
      : []),
    { label: 'Details submitted', people: ck.submittedSessions,       raw: null },
    { label: 'Payment failed',    people: ck.failedSessions,          raw: null },
    ...(ck.alreadyEnrolledSessions > 0
      ? [{ label: 'Already enrolled', people: ck.alreadyEnrolledSessions, raw: null }]
      : []),
    { label: 'Purchased',         people: ck.purchasedSessions,       raw: null },
  ] : [];

  // Only people who have NOT since bought are actionable. Recovered entries are
  // omitted rather than greyed out — this list exists to be worked through, and
  // a name on it that needs no action is worse than absent.
  const failedToFollowUp = (ck?.failedPayments || []).filter(f => !f.recovered);

  // ── THE TWO DENOMINATORS, NAMED ONCE ────────────────────────────────────────
  // Every depth percentage on both cards divides by one of these, and each is
  // passed EXPLICITLY at its call site. Nothing below reads a `.pct` the
  // endpoint computed, and nothing infers a denominator from position.
  //
  // That is not stylistic. The Funnel Overview divided each row by whatever row
  // happened to sit above it, so inserting a row silently re-pointed the one
  // below and it read wrong for a week. Naming the denominator at the point of
  // division is what makes that class of bug impossible here.
  //
  // WHICH FIGURES THESE ARE, so they can be checked against the screen:
  //   mcUniqueVisitors    = the masterclass card's "Unique visitors" tile
  //                         (wistia.masterclass.uniqueVisitorsAll)
  //   salesUniqueVisitors = the sales card's "Unique visitors" tile
  //                         (funnel.uniqueViewers)
  // Both are on screen, a few rows above the bars they now divide.
  const mcUniqueVisitors    = mc?.uniqueVisitorsAll ?? 0;
  const salesUniqueVisitors = sFunnel?.uniqueViewers ?? 0;

  // Scroll bars: cumulative "reached at least", as a share of unique visitors.
  //
  // The bar WIDTH is the percentage itself, not the count relative to the
  // biggest bucket as it was before. Under the old scaling the top bar was
  // always full width whatever it represented, so a bar reading "43%" was drawn
  // at 100% — the number and the picture said different things. They now say
  // the same thing, which is the only reason the width rule changed. No count
  // moved.
  const scrollBuckets = sFunnel ? [
    { label: 'Reached at least 25%', count: sFunnel.scroll?.p25?.count || 0 },
    { label: 'Reached at least 50%', count: sFunnel.scroll?.p50?.count || 0 },
    { label: 'Reached at least 75%', count: sFunnel.scroll?.p75?.count || 0 },
    { label: 'Reached at least 90%', count: sFunnel.scroll?.p90?.count || 0 },
  ].map(b => ({ ...b, pct: pctOf(b.count, salesUniqueVisitors) })) : [];

  // Ad Metrics only: how much of the picture is confident. paidTrace is null
  // unless paidOnly was sent, so nothing renders on Pages & Masterclass.
  const mcTrace = wistia?.paidTrace?.masterclass || null;

  const strip = [
    // Was a "needs sync" placeholder — the figure has been in this same
    // response as funnel.optin.pageLoads all along.
    { label: 'Opt-in views',  val: funnel?.optin?.pageLoads },
    { label: 'Opt-ins',       val: leads?.totalLeads },
    { label: 'Watched MC',    val: mc?.totalPlays,       sub: 'started' },
    { label: 'RY visitors',   val: sFunnel?.uniqueVisitors, sub: 'engaged', sales: true },
    // "Paid sales" meant paid-for (not comp/gift) — words that on the Ad
    // Metrics tab would read as "sales from paid traffic". Each tab now says
    // which it means.
    paidOnly
      ? { label: 'Sales from ads', val: sSales?.paidPurchasers, sub: 'attributed · from 16 Aug', sales: true }
      : { label: 'Paid-for sales', val: sSales?.paidPurchasers, sub: 'excl. gift & comp', sales: true },
  ];

  // ── THE FUNNEL STRIP'S FIVE STEPS ───────────────────────────────────────
  // Same field, same helper as the card each one sits above. In particular:
  //   step 1 is optin.uniqueVisitors, NOT pageLoads;
  //   step 3 is cta.clicked.count — people, the same figure the tile prints
  //           as "N of M unique visitors";
  //   step 4 is checkout.ctaClickSessions — unique clickers, NEVER the
  //           ctaByText click total the same tile shows as "N clicks".
  // Rates: step 2 is the opt-in card's own "Unique visitors → opt-ins"
  // (optinRate); step 3 is the masterclass tile's own "% of opt-ins"
  // (sharePct, the half of shareOfOptins that makes the number). Steps 4 and
  // 5 have no card equivalent and use the same sharePct rules.
  const mcClickers    = mc?.cta?.clicked?.count;
  const salesClickers = sFunnel?.checkout?.ctaClickSessions;
  // Steps 4 and 5 are SALES page figures, so they follow the switch — and so
  // must the bottom of every rate on them: masterclass clickers and opt-ins
  // from the SAME period (the sales-period fetch returns both).
  const mcClickersSalesPeriod = mcClickers;   // same range as the sales figures now
  const funnelSteps = paidOnly ? [
    { label: 'Unique visitors',        val: funnel?.optin?.uniqueVisitors },
    { label: 'Opt-ins',                val: leads?.totalLeads,
      of: 'unique visitors',           pct: optinRate(leads?.totalLeads, funnel?.optin?.uniqueVisitors),
      prevKnown: funnel?.optin?.uniqueVisitors != null },
    { label: 'Masterclass CTA clicks', val: mcClickers,
      of: 'opt-ins',                   pct: sharePct(mcClickers, leads?.totalLeads),
      prevKnown: leads?.totalLeads != null },
    // Steps 4 and 5 also carry a share of OPT-INS beneath the step-before
    // rate (Ben, 2026-09-28): same leads.totalLeads the Opt-ins step shows,
    // same date floor, same sharePct rules; both counts are PEOPLE.
    { label: 'Sales page CTA clicks',  val: salesClickers,
      of: 'masterclass CTA clicks',    pct: sharePct(salesClickers, mcClickersSalesPeriod),
      prevKnown: mcClickersSalesPeriod != null,
      also: [{ of: 'opt-ins', pct: sharePct(salesClickers, sLeads?.totalLeads), prevKnown: sLeads?.totalLeads != null }] },
    // The "attributed from 16 Aug" note was removed (2026-09-28). It was a
    // label only: sales here are counted from the 14 Aug launch floor and
    // traced by ry_users.visitor_id; no date filter ever read it.
    { label: 'Sales',                  val: sSales?.paidPurchasers,
      of: 'sales page CTA clicks',     pct: sharePct(sSales?.paidPurchasers, salesClickers),
      prevKnown: salesClickers != null,
      also: [{ of: 'opt-ins', pct: sharePct(sSales?.paidPurchasers, sLeads?.totalLeads), prevKnown: sLeads?.totalLeads != null }] },
    // COST PER OPT-IN (Ben, 2026-09-30): ALL Meta spend in this tab's range ÷
    // the Opt-ins step above — both from the same /leads-stats response, so the
    // same range; NOT the sales-video-period fetch, so the switch cannot move
    // it. Day mapping is decided server-side (metaAdsSummary adSpendForAdminRange).
    costPerOptInStep(leads, fxRates),
  ] : null;

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      <style>{`
        @keyframes shimmer { from { background-position: 200% 0 } to { background-position: -200% 0 } }
      `}</style>

      <SectionHeader
        title={paidOnly ? 'Ad Metrics' : 'Pages & Masterclass'}
        sub={paidOnly
          ? 'The same funnel, PAID TRAFFIC ONLY — people whose first opt-in landing carried a Meta ad tag. Landing counts are narrowed again to Starting 5; see the note on the opt-in card. Floored at 14 Aug 2026, like every figure here.'
          : 'Traffic, watch, and conversion across your funnel pages'}
      />

      {/* Trace completeness, stated where the figures are. Untraceable means an
          engaged visitor with NO opt-in landing at all — we cannot say whether
          an ad brought them, so they are excluded from every paid figure AND
          counted here rather than silently dropped. */}
      {paidOnly && mcTrace && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 11.5, color: C.text, lineHeight: 1.6 }}>
          <strong style={{ color: C.heading }}>Trace:</strong>{' '}
          {mcTrace.paid} of {mcTrace.engaged} engaged masterclass visitors came from ads
          {mcTrace.notPaid > 0 && <> · {mcTrace.notPaid} from other sources</>}
          {mcTrace.untraceable > 0
            ? <> · <strong style={{ color: C.heading }}>{mcTrace.untraceable} can’t be traced</strong> (no opt-in landing — excluded from the paid figures, not counted as ads)</>
            : <> · 0 untraceable</>}
          . Sales attribution runs from <strong style={{ color: C.heading }}>16 Aug 2026</strong> forward — earlier buyers carry no visitor id, so they can never appear here.
        </div>
      )}

      {/* ── One date filter for the whole tab, with the dates it covers ── */}
      <TimeframeControlWithDates
        range={range} setRange={setRange}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
      />

      {/* ── Which sales video — sets the period for EVERY sales page figure ── */}

      {/* ── The funnel in one line — Ad Metrics only, additive, above all else ── */}
      {funnelSteps && <FunnelStrip steps={funnelSteps} loading={loading} failed={!!error} />}

      {error && (
        <div style={{ background: C.redBg, border: `1px solid #FECACA`, borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.red }}>
          ✕ {error}
        </div>
      )}

      {/* ── Funnel at a glance ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '14px 18px', marginBottom: 20,
      }}>
        {strip.map((s, i) => (
          <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {i > 0 && <span style={{ color: C.border, fontSize: 16 }}>›</span>}
            <span style={{ textAlign: 'center' }}>
              <span style={{ display: 'block', fontSize: 18, fontWeight: 800, color: C.heading }}>
                {loading ? '…' : (s.val ?? 0).toLocaleString()}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {s.label}{s.sub && <span style={{ textTransform: 'none', letterSpacing: 0 }}>({s.sub})</span>}
              </span>
            </span>
          </span>
        ))}
      </div>
      <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', margin: '-14px 4px 20px', lineHeight: 1.55 }}>
        Visitor counts include only engaged visitors — scrolled, clicked, played or purchased. Passive read-and-leave visits aren't counted.
        {/* ONE line, placed above all three columns so it covers the masterclass
            AND sales cards without repeating itself inside each one. */}
        {' '}Each one, and each CTA-clicker count, also shows what share of opt-ins it represents. That compares visitors,
        counted by device, with people, counted once each — so treat it as a guide rather than an
        exact ratio. It can pass 100%, because a page can be reached from a direct link without
        opting in first.
      </div>

      {/* ── Three columns ── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>

        {/* OPT-IN */}
        <PageColumn accent={ACCENT.optin} badge="Opt-in" title="go.benmilston.com" note="Where ads and links land — collects the opt-in">
          {loading ? (<><Shimmer h={52} mb={10} /><Shimmer h={80} /></>) : leads ? (
            <>
              {/* Stays 3-across: the masterclass column already ships
                  "Engaged visitors" at this same width, so a two-word label is
                  proven to fit on a phone. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                <Tile label="Page loads" val={(funnel?.optin?.pageLoads ?? 0).toLocaleString()} />
                <Tile label="Unique visitors" val={(funnel?.optin?.uniqueVisitors ?? 0).toLocaleString()} />
                {/* + returning sign-ups: filled in the form again this period
                    after signing up before. Beside the count, never in it
                    (backend services/returningSignups.js); same range, paid
                    rule and exclusions as the tile. null = not read, no line. */}
                <Tile label="Opt-ins" val={leads.totalLeads.toLocaleString()}
                  sub={leads.returningSignups != null ? returningLine(leads.returningSignups) : undefined} />
              </div>
              {/* Two conversion rates, computed from the THREE TILES ABOVE so the
                  rates and the tiles can never disagree. The line that was here
                  disclaimed a 400 pre-tracking baseline; that baseline is now 0
                  (the API returns optin.baseline: 0, and pageLoads equals
                  pageLoadsTracked), so the disclaimer described something that
                  no longer happens. */}
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: -6, marginBottom: 12 }}>
                <span>Page loads → opt-ins: <strong style={{ color: C.heading, fontStyle: 'normal' }}>{optinRate(leads.totalLeads, funnel?.optin?.pageLoads)}</strong></span>
                <span>Unique visitors → opt-ins: <strong style={{ color: C.heading, fontStyle: 'normal' }}>{optinRate(leads.totalLeads, funnel?.optin?.uniqueVisitors)}</strong></span>
              </div>
              {/* WHY THIS TAB'S LANDING FIGURES ARE SMALLER THAN PAGES &
                  MASTERCLASS. Without this line, Ben opens the two tabs in three
                  months, sees two different landing counts for the same page,
                  and has no way to tell which is broken. It also states the
                  asymmetry he chose: landings are narrowed, opt-ins are not.
                  Ad Metrics only — nothing renders on Pages & Masterclass. */}
              {paidOnly && (
                <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', lineHeight: 1.55, marginTop: -4, marginBottom: 12 }}>
                  Landings here are <strong style={{ color: C.heading, fontStyle: 'normal' }}>Starting 5 only</strong> — the campaign
                  built to convert. Power Content and 4A warm the audience and aren’t meant to opt anyone in, so counting their
                  traffic would make this page look like it converts worse than it does. Opt-ins still count every campaign, so if
                  one ever comes from those two the rate reads slightly high rather than low.{' '}
                  <strong style={{ color: C.heading, fontStyle: 'normal' }}>Pages &amp; Masterclass</strong> shows all traffic.
                </div>
              )}
              <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: 11, color: C.accent }}>
                <span>This month: <strong style={{ color: C.heading }}>{leads.thisMonthLeads}</strong></span>
                <span>Last month: <strong style={{ color: C.heading }}>{leads.lastMonthLeads}</strong></span>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: '0.07em', marginBottom: 8, textTransform: 'uppercase' }}>
                  Daily opt-ins (last 7 days)
                </div>
                {chartData.length > 0 && (
                  <MiniBarChart data={chartData} valueKey="count" labelKey="date" height={72} />
                )}
              </div>
            </>
          ) : <div style={{ fontSize: 12, color: C.red }}>Could not load opt-in data</div>}
        </PageColumn>

        {/* MASTERCLASS */}
        <PageColumn accent={ACCENT.mc} badge="Masterclass" title="when-awareness-isnt-enough.benmilston.com" note="Mostly reached by opting in — but the link works without">
          {loading ? (<><Shimmer h={52} mb={10} /><Shimmer h={120} /></>) : mc ? (
            <>
              {/* Funnel order: loads, then people, then watchers, then rate.
                  Page loads counts LOADS and is not deduped; Engaged visitors
                  counts PEOPLE who did something, so loads is always the larger
                  of the two. The opt-in and RY columns both lead with page
                  loads, so this column now matches them. Two rows of two rather
                  than one of four — four tiles across is unreadable on a
                  phone. */}
              {/* ONE GRID, NOT THREE. Three separate 2-column grids of 3, 2 and 2
                  tiles left a hole beside the third tile — mid-card, where it
                  reads as a missing number rather than the end of a list. Merged,
                  the tiles flow and only the final row can be short, however many
                  there are. Order, styling and gap are unchanged. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <Tile label="Page loads" val={(mc.pageLoads ?? 0).toLocaleString()} />
                {/* Same middle figure as the sales card. These three are one
                    population narrowing: every load, the people behind them,
                    then the people who did something.
                    ⚠ This card does NOT reconcile with the Funnel Overview and
                    cannot: it splices its loads at the funnel cutover and
                    applies no bot filter, while the Overview does neither. That
                    divergence already governed pageLoads before this figure
                    existed; it inherits it rather than introducing it. */}
                <Tile label="Unique visitors" val={(mc.uniqueVisitorsAll ?? 0).toLocaleString()} />
                {/* DELIBERATELY STILL A SHARE OF OPT-INS. This one answers a
                    different question — how much of the list got here — and Ben
                    wants it left alone. It is the only percentage on this card
                    that is not a share of unique visitors. */}
                <Tile label="Engaged visitors" val={mc.totalSessions.toLocaleString()}
                  sub={shareOfOptins(mc.totalSessions, leads?.totalLeads)} />
                {/* THE SEEN-TIME MEASURE (2026-09-28): a watcher STARTED —
                    interacted with the player or saw 15 seconds. */}
                <Tile label="Started watching" val={mc.totalPlays.toLocaleString()}
                  sub={mc.playsAnyStart != null ? `${mc.playsAnyStart} autoplay starts` : undefined} />
                {(() => {
                  // Named once, used by all three percentages below, so they
                  // cannot drift onto different denominators.
                  const uv = mc.uniqueVisitorsAll ?? 0;
                  const watchPct = pctOf(mc.totalPlays, uv);
                  return (
                    <Tile label="Watch rate" val={`${watchPct}%`}
                      sub={ofUniqueVisitors(mc.totalPlays, uv)}
                      accent={watchPct >= 50 ? C.green : watchPct >= 30 ? C.amber : C.red} />
                  );
                })()}
                {mc.cta && (() => {
                  const uv = mc.uniqueVisitorsAll ?? 0;
                  const clickedPct = pctOf(mc.cta.clicked.count, uv);
                  return (
                    <>
                      <Tile label="Reached CTA" val={`${pctOf(mc.cta.revealed.count, uv)}%`}
                        sub={ofUniqueVisitors(mc.cta.revealed.count, uv)} />
                      {/* Two lines, two denominators, one numerator. The first
                          is the page basis every tile on this card uses; the
                          second reads the SAME clicker count against the
                          Opt-ins tile on this tab, the way the ENGAGED VISITORS
                          tile above does. mc.cta.clicked.count is the endpoint's
                          person-deduped figure — nothing is recounted here. */}
                      <Tile label="Clicked CTA" val={`${clickedPct}%`}
                        sub={<>{ofUniqueVisitors(mc.cta.clicked.count, uv)}<br />{clickersShareOfOptins(mc.cta.clicked.count, leads?.totalLeads)}</>}
                        accent={clickedPct >= 10 ? C.green : clickedPct >= 5 ? C.amber : undefined} />
                    </>
                  );
                })()}
              </div>
              {/* The drop-off link rides on this heading rather than taking a
                  row of its own, so the 2x2 tile grid above is untouched. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                {/* ── THE HEADING NAMES THE DENOMINATOR ────────────────────
                    It read "of {N} who watched 15s+", which was the old
                    denominator. The bars beneath now divide by unique visitors,
                    so leaving that heading would have made it actively wrong —
                    a label describing a division the code no longer performs.
                    It names the new denominator instead, and the same figure is
                    on screen in the "Unique visitors" tile directly above. */}
                <div style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Watch depth — of {mcUniqueVisitors} unique visitors
                </div>
                {/* The drop-off modal draws its line against the people who
                    started watching (a retention curve), on the card's own
                    seen-time measure, and states the share of these same
                    unique visitors beside every point (2026-10-01). */}
                <DropOffLink
                  title="Masterclass — where people drop off"
                  curve={mc.seenCurve}
                  landed={mcUniqueVisitors}
                  startedNote="Started = pressed play, paused, skipped, unmuted or changed speed, or saw 15 seconds (the video autoplays)."
                />
              </div>
              {/* Ben: "eighty percent of unique visitors that actually land on
                  the page, eighty percent of them make it to at least 25%".
                  These divided by totalPlays — the people who had already
                  watched 15s+ — which flattered every bar, exactly as the
                  headline tiles did before 8757de9.

                  COUNTS ARE UNTOUCHED. Only the divisor moves. Measured on one
                  snapshot, 2026-09-02 09:07Z, Ad Metrics: 25% stays 32 people
                  and reads 41% of 78 rather than 68% of 47; 50% stays 29 (37%);
                  75% stays 25 (32%); 90% stays 21 (27%); end stays 16 (21%).

                  THE DENOMINATOR CANNOT BE EXCEEDED. Every milestone count is
                  taken from `plays`, which is a subset of the engaged set, which
                  is a subset of `byVisitor` — and byVisitor.size IS
                  uniqueVisitorsAll. So numerator ⊆ denominator by construction:
                  no bar here can print above 100%, and the funnel can only
                  descend. */}
              {/* Bars are the share of the video actually SEEN (seen-time
                  measure); the last bar is 95%+ seen, named by the endpoint. */}
              <MilestoneBar label="Under 25% seen" pct={pctOf(under25Of(mc), mcUniqueVisitors)} count={under25Of(mc)} />
              <MilestoneBar label="25% seen" pct={pctOf(mc.milestones.p25.count, mcUniqueVisitors)} count={mc.milestones.p25.count} />
              <MilestoneBar label="50% seen" pct={pctOf(mc.milestones.p50.count, mcUniqueVisitors)} count={mc.milestones.p50.count} />
              <MilestoneBar label="75% seen" pct={pctOf(mc.milestones.p75.count, mcUniqueVisitors)} count={mc.milestones.p75.count} />
              <MilestoneBar label="90% seen" pct={pctOf(mc.milestones.p90.count, mcUniqueVisitors)} count={mc.milestones.p90.count} />
              <MilestoneBar label={mc.measure?.lastMilestone ?? 'Reached end'} pct={pctOf(mc.milestones.end.count, mcUniqueVisitors)} count={mc.milestones.end.count} />
              <Under25Note v={mc} />
              <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.accent }}>
                Avg time seen: <strong style={{ color: C.heading }}>{fmtTime(mc.avgSecondsWatched)}</strong>
                {mc.avgDurationSecs > 0 && <span style={{ marginLeft: 8 }}>· Video length: <strong style={{ color: C.heading }}>{fmtTime(mc.avgDurationSecs)}</strong></span>}
              </div>
              {/* REWRITTEN because its second clause — "every number above
                  counts only visitors who watched at least 15 seconds" — was a
                  statement about the OLD denominator and became false the moment
                  the bars started dividing by unique visitors. The 15s figure is
                  still worth having, so it is kept as a fact about the watchers
                  rather than as the basis of the bars. */}
              {/* THE MEASURE, STATED ON THE CARD (2026-09-28). Two different
                  measures are never shown under one label: visitors recorded
                  before the seen-time measure began are counted in the
                  visitor tiles but excluded from every watch figure, and the
                  card says how many. */}
              <div style={{ paddingTop: 8, fontSize: 9, color: C.accent, fontStyle: 'italic', lineHeight: 1.5 }}>
                Watching is measured by time actually seen — the unique seconds of the video that played;
                skipping ahead and rewatching don't count, muted playback does.
                {mc.measure?.since ? <> Exact from <strong style={{ color: C.heading }}>{new Date(mc.measure.since).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</strong>;
                  before that, <strong style={{ color: C.heading }}>estimated</strong> from the 30-second segments each visit played through, with jumps removed
                  {mc.measure.personsEstimated != null && <> ({mc.measure.personsEstimated} of {mc.totalPlays} watchers here are estimated)</>}.</> : null}
                {' '}A watcher pressed play, paused, skipped, unmuted or changed speed, or saw 15 seconds
                (this video autoplays when it scrolls into view, so a "play" on its own isn't a choice).
                {' '}{mc.totalPlays} of {mcUniqueVisitors} unique visitors started watching; the bars above
                are a share of all {mcUniqueVisitors}, not of those {mc.totalPlays}.
                {' '}Separately, an opt-in is flagged "Masterclass Watched" in Contacts and the
                exported report at 25% seen, so that number is smaller by design.
              </div>
            </>
          ) : <div style={{ fontSize: 12, color: C.red }}>Could not load masterclass data</div>}
        </PageColumn>

        {/* RY SALES */}
        <PageColumn accent={ACCENT.ry} badge="Sales" title="recondition-yourself.benmilston.com" note="Not gated — people are sent here directly too">
          {sError ? (
            <div style={{ fontSize: 11.5, color: C.red }}>✕ {sError}</div>
          ) : sLoading ? (<><Shimmer h={52} mb={10} /><Shimmer h={120} /></>) : (
            <>
              {/* 2x2, not 4-across. PageColumn is minWidth 280 with 20px side
                  padding, so the inner width bottoms out at 240px: four tiles
                  with an 8px gap would be (240-24)/4 = 54px each, ~38px of
                  content after the tile's own padding — "ENGAGED VISITORS" would
                  wrap to three lines on a phone. Two columns gives 116px each. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <Tile label="Page loads" val={(sFunnel?.pageViews ?? 0).toLocaleString()} />
                {/* THE MIDDLE FIGURE. Loads sat straight next to ENGAGED
                    visitors, so "how many made it to the page" and "how many did
                    something on it" read as one number. These are the same
                    people as Page loads, deduped — nothing here redefines the
                    engaged figure or its share of opt-ins.
                    Reconciles exactly with the Funnel Overview's "Unique
                    visitors to sales page" — same table, floor, filters and
                    person key. */}
                <Tile label="Unique visitors" val={(sFunnel?.uniqueViewers ?? 0).toLocaleString()} />
                <Tile label="Engaged visitors" val={(sFunnel?.uniqueVisitors ?? 0).toLocaleString()}
                  sub={shareOfOptins(sFunnel?.uniqueVisitors, sLeads?.totalLeads)} />
                {/* ── CTA RATE — CLICKERS over unique visitors ────────────────
                    This tile used to be RAW CLICKS with no percentage, and that
                    was deliberate: 15 clicks against 34 people is not a rate,
                    and dividing actions by people can print >100%.

                    So the percentage divides CLICKERS by unique visitors —
                    13 of 34 = 38% — the one pair of figures that can honestly
                    make a rate. Both numbers describe PEOPLE and the clickers
                    set is a subset of the visitors set, so it can never exceed
                    100%.

                    THE CLICKERS FIGURE IS NOT COMPUTED HERE. It is
                    checkout.ctaClickSessions, which the endpoint already
                    returns and which is the SAME computation the Funnel
                    Overview's "Clicked sales CTA" row runs — same table, same
                    launch floor, same isBot/isBenVisitor filters, same
                    resolveFrom person key over funnel_stage 'ry_landing'
                    cta_click rows. Reconciled by hand against the database on
                    2026-09-01: both 13. Summing ctaByText[].uniqueVisitors
                    would be the second implementation and it would be WRONG —
                    it double-counts anyone who clicks two different buttons.

                    Denominator is uniqueViewers (34), the "Unique visitors"
                    tile two along, NOT engaged visitors (25) — the same call
                    8757de9 made on the masterclass card, and it is named in
                    the sub-line so it can never be read as a share of
                    something else.

                    SHARE OF OPT-INS, middle line: the same `clickers` figure
                    read against leads.totalLeads — the Opt-ins tile on this
                    tab — via clickersShareOfOptins, so it is written exactly as
                    the ENGAGED VISITORS line above writes its share. Not the
                    raw click total: that can exceed the opt-in count on its
                    own and would not be a share of anything.

                    RAW CLICKS ARE KEPT, on the sub-line: clicks show
                    enthusiasm, clickers show reach, and the total appears
                    nowhere else on the page — the "Which CTA they click"
                    breakdown below lists clicks per button, never their sum.
                    Same reduce over ctaByText as before, unmoved at 15.

                    THE LINE BREAK IS EXPLICIT because at 375px the tile is
                    ~116px wide and one run of text broke mid-fact — "13 of 34
                    unique visitors · 15" / "clicks". Two deliberate lines put
                    the break between the two facts instead of inside one.

                    Accent thresholds match the masterclass card's Clicked CTA
                    tile exactly, so the same number is coloured the same way on
                    both cards. */}
                {(() => {
                  const uv       = sFunnel?.uniqueViewers ?? 0;
                  const clickers = sFunnel?.checkout?.ctaClickSessions ?? 0;
                  const clicks   = sFunnel?.ctaByText?.reduce((sum, r) => sum + r.clicks, 0) ?? 0;
                  const pct      = pctOf(clickers, uv);
                  return (
                    <Tile label="Clicked a CTA" val={`${pct}%`}
                      sub={<>{ofUniqueVisitors(clickers, uv)}<br />{clickersShareOfOptins(clickers, sLeads?.totalLeads)}<br />{clicks.toLocaleString()} clicks</>}
                      accent={pct >= 10 ? C.green : pct >= 5 ? C.amber : undefined}
                      onClick={() => setCtaOpen(o => !o)} open={ctaOpen} />
                  );
                })()}
                {paidOnly
                  ? <Tile label="Sales from ads" val={(sSales?.paidPurchasers ?? 0).toLocaleString()}
                      sub="attributed · from 16 Aug 2026" />
                  : <Tile label="Paid-for sales" val={(sSales?.paidPurchasers ?? 0).toLocaleString()}
                      sub="excl. gift & comp" />}
              </div>
              {ctaOpen && <InterstitialPanel data={interstitial} failed={interstitialFailed} />}
              {sSales && (
                <div style={{ fontSize: 10, color: C.accent, marginBottom: 12 }}>
                  {/* NOT "all-time". /ry-sales-stats returns a WINDOW-SCOPED
                      totalPurchasers (backend/src/routes/admin.js:2069) — it
                      honours whatever timeframe is selected above. The old
                      wording claimed an all-time figure while showing a
                      windowed one, which was wrong on every range but "All
                      time". Label only; the number is unchanged. */}
                  {sSales.totalPurchasers} enrolled in this timeframe: {sSales.paidPurchasers} paid
                  <span style={{ margin: '0 4px', color: C.border }}>·</span>{sSales.giftPurchasers ?? 0} gift
                  <span style={{ margin: '0 4px', color: C.border }}>·</span>{sSales.compPurchasers} comp
                </div>
              )}
              {vw.failed ? (
                <div style={{ fontSize: 11, color: C.accent }}>Couldn’t load which sales video was live when, so the video figures are not shown. Reload to try again.</div>
              ) : !videoBlocks ? (
                <Shimmer h={90} mb={10} />
              ) : videoBlocks.length === 0 || videoBlocks.every(b => !b.ry) ? (
                <div style={{ fontSize: 11, color: C.accent }}>No sales-page video data yet</div>
              ) : (
                <>
                  {videoBlocks.length > 1 && (
                    <div style={{ fontSize: 9.5, color: C.accent, fontStyle: 'italic', marginBottom: 8, lineHeight: 1.5 }}>
                      This range spans the change of sales video. The two videos are different lengths, so each is shown on its own — counted only over the part of the range it was live.
                    </div>
                  )}
                  {videoBlocks.map(b => (
                    <SalesVideoBlock key={b.video.id} ry={b.ry} uv={b.uniqueViewers}
                      label={videoBlocks.length > 1 ? b.label : null} />
                  ))}
                </>
              )}
              {/* Scroll depth — equal billing with the video: scrolling and
                  watching are parallel ways of engaging with the sales page. */}
              {/* Heading names the denominator, same as the two watch-depth
                  headings above it. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '12px 0 8px' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  How far people scroll — of {salesUniqueVisitors} unique visitors
                </div>
                {/* The finer picture (every 10% + each section + who), opened
                    in the same panel pattern as "View drop-off", for exactly
                    the window, paid flag and video this card uses. */}
                {tfReady && (
                  <HeatMapLink from={from} to={to} paidOnly={paidOnly} rangeText={rangeLabel(range)} />
                )}
              </div>
              {sFunnel ? (
                <>
                  {/* Ben: "That should also be the case with the scroll — a
                      percentage of unique visitors." These rows printed a bare
                      count and no percentage at all, so there was nothing to
                      repoint; the percentage is new.

                      RAW COUNT KEPT, in the same pct · count order MilestoneBar
                      uses directly above, so the two depth sections read as one
                      idea rather than two.

                      SAFE BY CONSTRUCTION, unlike the video bars: scroll counts
                      and salesUniqueVisitors are both built from the SAME
                      filtered `rows` array inside /funnel-page-stats — one
                      launch floor, one isBot pass, one isBenVisitor pass, one
                      resolveFrom person key. Same table, same population. */}
                  {scrollBuckets.map(b => (
                    <div key={b.label} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.accent, marginBottom: 3 }}>
                        <span>{b.label}</span>
                        <span>
                          <strong style={{ color: C.heading }}>{b.pct}%</strong>
                          <span style={{ color: C.border, margin: '0 3px' }}>·</span>
                          <span style={{ color: C.accent }}>{b.count}</span>
                        </span>
                      </div>
                      <div style={{ background: C.barTrack, borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, b.pct)}%`, height: '100%', background: ACCENT.ry, opacity: 0.75, borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: 9, color: C.accent, fontStyle: 'italic' }}>
                    Visitors who scrolled to at least each depth (cumulative), as a share of
                    all {salesUniqueVisitors} unique visitors to the page. The page reports
                    25/50/75/90% only.
                  </div>
                </>
              ) : <div style={{ fontSize: 11, color: C.accent }}>No scroll data yet</div>}
            </>
          )}
        </PageColumn>
      </div>

      {/* Sits directly under the three page columns, so the phone share reads
          against the arrival counts it is a share OF. */}
      <DeviceSplit
        optin={funnel?.optin?.devices}
        masterclass={mc?.devices}
        ry={sFunnel?.devices}
      />

      {/* ── Deeper detail ── */}
      <div style={{ fontSize: 12, fontWeight: 800, color: C.heading, marginBottom: 10 }}>Deeper detail</div>

      {/* The three "By source" placeholders that sat here promised a table
          "once the Meta/GA sync is built". That sync exists now, and its data
          already renders elsewhere — source-by-source landings and opt-ins on
          Traffic & UTM (the campaign drill in the opt-in table), spend and ad
          performance on Ad Metrics. A pointer beats a stale promise; a third
          copy of the same tables here would just be another thing to keep in
          sync. */}
      <div style={{ fontSize: 11, color: C.accent, lineHeight: 1.6, margin: '0 0 14px', padding: '10px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        Source-by-source breakdowns live on <strong style={{ color: C.heading }}>Traffic &amp; UTM</strong> (where
        each campaign's landings came from) and <strong style={{ color: C.heading }}>Ad Metrics</strong> (what the
        ads cost and did).
      </div>

      {/* NO BADGE. This panel now covers both pages, so "SALES" on its header
          would be wrong the moment the Masterclass tab is open. */}
      <DetailRow accent={ACCENT.ry} title="Which CTA they click">
        <PageSubTabs value={whichCtaPage} onChange={setWhichCtaPage} />

        {/* BOTH PAGES NOW RUN THROUGH THE SAME COMPONENT. The sales view used
            to be a separate block grouping funnel.ctaByText by button text;
            that grouping merged five different buttons reading "ACCESS NOW" or
            "SIGN ME UP!" into two rows and could never say which one was
            actually pressed. One component means the two tabs cannot drift
            apart in wording or arithmetic. */}
        <CtaTagBreakdown
          data={whichCtaPage === 'masterclass' ? ctaDetail?.masterclass : sCtaDetail?.[whichCtaPage]}
          failed={whichCtaPage === 'masterclass' ? ctaDetailFailed : sCtaDetailFailed}
          pageLabel={whichCtaPage === 'masterclass' ? 'masterclass' : 'sales-page'}
          paidCaveat={paidOnly && whichCtaPage === 'masterclass'}
          periodFrom={from}
          paidOnly={paidOnly}
        />
      </DetailRow>

      {/* ── WHERE IN THE VIDEO THE CTA WAS CLICKED ─────────────────────────────
          One mark per click against that page's own video length. See
          VideoClickPlot for why this is not a histogram. */}
      <DetailRow accent={ACCENT.ry} title="Where in the video the CTA was clicked">
        <PageSubTabs value={videoCtaPage} onChange={setVideoCtaPage} />
        {videoCtaPage === 'masterclass' ? (
          ctaDetailFailed ? (
            <div style={{ fontSize: 11, color: C.accent }}>Could not load CTA video positions.</div>
          ) : !ctaDetail ? (
            <div style={{ fontSize: 11, color: C.accent }}>Loading…</div>
          ) : renderVideoClicks(ctaDetail.masterclass, true, rangeLabel(range))
        ) : vw.failed ? (
          <div style={{ fontSize: 11, color: C.accent }}>Couldn’t load which sales video was live when, so the video positions are not shown.</div>
        ) : !videoBlocks ? (
          <div style={{ fontSize: 11, color: C.accent }}>Loading…</div>
        ) : videoBlocks.length === 0 ? (
          <div style={{ fontSize: 11, color: C.accent }}>No sales page video in this date range.</div>
        ) : (
          videoBlocks.map(b => (
            <div key={b.video.id} style={{ marginBottom: videoBlocks.length > 1 ? 18 : 0 }}>
              {videoBlocks.length > 1 && (
                <div style={{ fontSize: 10.5, fontWeight: 800, color: C.red, marginBottom: 6 }}>{b.label}</div>
              )}
              {b.cta
                ? renderVideoClicks(b.cta, false, videoBlocks.length > 1 ? b.label : rangeLabel(range))
                : <div style={{ fontSize: 11, color: C.accent }}>Could not load CTA video positions.</div>}
            </div>
          ))
        )}
      </DetailRow>

      <DetailRow accent={ACCENT.ry} badge="Sales" title="Checkout drop-off">
        {sFunnel?.checkout ? (
          <>
            {checkoutFunnelRows.map(r => (
              <div key={r.label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.barTrack}`, fontSize: 12,
              }}>
                <span style={{ fontWeight: 600, color: C.text }}>{r.label}</span>
                <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap' }}>
                  {/* A row whose event did not exist for the whole window
                      prints a dash and the date it started, never a count.
                      Everything else renders exactly as it always has. */}
                  {r.unavailable ? (
                    <><strong style={{ color: C.heading }}>—</strong> <span>{r.unavailableNote}</span></>
                  ) : (
                    <>
                      <strong style={{ color: C.heading }}>{r.people}</strong> {r.people === 1 ? 'person' : 'people'}
                      {r.raw != null && r.raw !== r.people && <span> ({r.raw} {r.rawNoun})</span>}
                      {r.pctDenom != null && r.pctDenom > 0 && (
                        <span> ({pctOf(r.people, r.pctDenom)}% of {r.pctNoun})</span>
                      )}
                    </>
                  )}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: 10 }}>
              Checkout tracking started 14 Aug
            </div>
            {sFunnel.checkout.purchasedLegacyExcluded > 0 && (
              <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: 4 }}>
                {sFunnel.checkout.purchasedLegacyExcluded} earlier purchase event
                {sFunnel.checkout.purchasedLegacyExcluded === 1 ? ' is' : 's are'} excluded — they predate the
                fix and counted repeat sign-ins as purchases.
              </div>
            )}
          </>
        ) : <div style={{ fontSize: 11, color: C.accent }}>No checkout data yet</div>}
      </DetailRow>

      <DetailRow accent={ACCENT.ry} badge="Sales" title="Failed payments">
        {failedToFollowUp.length ? (
          <>
            <div style={{ fontSize: 10, color: C.accent, marginBottom: 10 }}>
              People whose payment failed and who have not bought since. Most recent first.
            </div>
            {failedToFollowUp.map((f, i) => (
              <div key={(f.session_id || '') + i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.barTrack}`, fontSize: 12, flexWrap: 'wrap',
              }}>
                <span style={{ minWidth: 0 }}>
                  <strong style={{ color: C.heading }}>{f.firstName || 'Unknown'}</strong>
                  <span style={{ color: C.accent, wordBreak: 'break-all' }}> {f.email || '(no email captured)'}</span>
                </span>
                <span style={{ fontSize: 11, color: C.accent, whiteSpace: 'nowrap' }}>
                  {failureReasonLabel(f)}
                  <span style={{ margin: '0 4px', color: C.border }}>·</span>
                  {new Date(f.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
          </>
        ) : (
          <div style={{ fontSize: 11, color: C.accent }}>
            None — nobody's payment has failed since tracking started.
          </div>
        )}
      </DetailRow>
    </div>
  );
};
